import type { LangDict } from "./types";

// Strings for owner-only screens (Home, Listings, Ledgers, Reports,
// Requests, and the property/tenant forms). Keep namespace keys unique
// across core.ts / owner.ts / tenant.ts since they're merged flat.
export const owner: LangDict = {
  en: {
    ownerHome: {
      sectionTitlePg: "Your PGs",
      sectionTitleProperties: "Your Properties",
      thisMonth: "This month",
      pendingThisMonth: "Pending this month",
      alreadyReceivedLine: "{amount} already received · {pct}% collected",
      allCollectedLine: "All collected this month",
      receivedLabel: "Received",
      pendingLabel: "Pending",
      collectedPill: "{pct}% Collected",
      paidInFullCaption: "Paid in full",
      dueSoonCaption: "Due soon",
      bedsFilledLine: "{filled} of {total} beds filled",
      tenantsToFollowUp: "Tenants to follow up",
      propertiesTileLabel: "Properties",
      tenantsHeadingLabel: "Tenants",
      viewAll: "View all",
      allCaughtUp: "Nobody's pending this month — nice.",
      paymentMarkedToast: "Payment marked as received",
      reminderSentToast: "Reminder sent",
      pctCollected: "{pct}% collected",
      received: "Received",
      pending: "Pending",
      remindAll: "Remind All",
      remindersSent: "Reminders sent to {count} tenant(s)",
      couldNotSendReminders: "Could not send reminders",
      bedOccupancy: "Bed Occupancy",
      bedsFraction: "{filled}/{total} beds",
      acrossPg: "Across {count} PG {unit}",
      pgUnitSingular: "property",
      pgUnitPlural: "properties",
      totalCount: "{count} total",
      addPropertyAria: "Add property",
      addUnitFab: "Add Unit",
      sendWhatsappReceiptBtn: "Send WhatsApp Receipt",
      directUpiPayLinkBtn: "Direct UPI Pay Link",
      whatsappReceiptMessage: "Hi {tenant}, here's your rent receipt for {property}: {amount} ({status}). - {owner}, via ToLetBook",
      upiPayLinkMessage: "Hi {tenant}, please pay this month's rent for {property} ({amount}) via UPI: {link} - {owner}, via ToLetBook",
      noTenantPhone: "No phone number on file for this tenant",
      noUpiIdSet: "Add your UPI ID in Settings first",
      tabProperty: "Property",
      tabPg: "PG",
      emptyNoProperties: "No properties yet. Tap + to add your first one.",
      emptyNoIndependent: "No independent properties yet.",
      emptyNoPg: "No PG properties yet.",
      emptyNoCommercial: "No commercial properties yet.",
      filterAll: "All",
      filterResidential: "Residential",
      filterCommercial: "Commercial",
      filterPg: "PG / Hostel",
      sqftSuffix: "sq ft",
      listedOnMarketplace: "Listed on the marketplace",
      removedFromMarketplace: "Removed from the marketplace",
      couldNotUpdateListing: "Could not update listing",
      rentUpdated: "Rent updated",
      couldNotUpdateRent: "Could not update rent",
      maintenanceUpdated: "Maintenance updated",
      couldNotUpdateMaintenance: "Could not update maintenance",
      paid: "Paid",
      tenantLabel: "Tenant",
      perMonth: "/mo",
      phoneLabel: "Phone",
      noPhoneOnFile: "No phone on file",
      aadhaarLabel: "Aadhaar",
      depositLabel: "Deposit",
      moveInDateLabel: "Move-in date",
      hideManageOptions: "Hide manage options",
      manage: "Manage",
      manageHouses: "Manage houses",
      rentalAgreement: "Rental Agreement",
      rentalReceipts: "Rental Receipts",
      modifyRent: "Modify Rent",
      maintenanceMenu: "Maintenance",
      removeTenant: "Remove Tenant",
      newRentLabel: "New rent / month",
      save: "Save",
      maintenancePerMonthLabel: "Maintenance / month",
      vacantBadge: "Vacant",
      expectedRentLabel: "Expected rent",
      depositSuffix: " · Deposit {amount}",
      maintenanceLine: "+ {amount}/mo maintenance",
      listOnToLet: "List on TO-LET marketplace",
      liveVisible: "Live · visible to renters",
      notListedYet: "Not listed yet",
      toggleToLetAria: "Toggle TO-LET",
      toLetStatus: "TO-LET status",
      housesLiveLine: "{count} of {total} houses live",
      manageToLetAria: "Manage TO-LET listing",
      addTenantButton: "Add Tenant",
      occBoys: "Boys PG",
      occGirls: "Girls PG",
      occCoEd: "Co-ed PG",
      occDefault: "PG",
      listVacantBedsToLet: "List vacant beds on TO-LET",
      fullyOccupied: "Fully occupied · no vacant beds",
    },
    ownerListings: {
      propertyAdded: "Property added",
      upgradeToastMsg: "You've reached your plan's listing limit — redirecting to upgrade options.",
      heading: "My Properties",
      addLink: "+ Add ›",
      addPropertyAria: "Add property",
      emptyState: "No properties yet — add your first one.",
      confirmDelete: 'Delete "{label}"? This cannot be undone.',
      propertyDeleted: "Property deleted",
      confirmDeleteHouse: 'Remove "{label}"? This cannot be undone.',
      houseRemoved: "House removed",
      houseUpdated: "House details updated",
      listedOnMarketplace: "Listed on the marketplace",
      removedFromMarketplace: "Removed from the marketplace",
      couldNotUpdateListing: "Could not update listing",
      vacantBedsFraction: "{vacant} / {total} vacant",
      listOnToLet: "List on TO-LET marketplace",
      listVacantBedsToLet: "List vacant beds on TO-LET",
      liveVisible: "Live · visible to renters",
      notListedYet: "Not listed yet",
      toggleToLetAria: "Toggle TO-LET",
      moreOptionsAria: "More options",
      editDetails: "Edit details",
      deleteProperty: "Delete property",
      expectedRentLabel: "Expected rent",
      depositLabel: "Deposit",
      noPhoneOnFile: "No phone on file",
      perMonth: "/mo",
      endTenancyButton: "End tenancy",
      editTenantButton: "Modify tenant",
      saveTenantButton: "Save changes",
      tenantAdded: "Tenant added",
      tenantUpdated: "Tenant details updated",
      roomAdded: "Room added",
      couldNotAddRoom: "Could not add room",
      addTenantLink: "+ Add tenant",
      installAppPromptTitle: "{name} hasn't installed ToLetBook yet",
      installAppPromptBody: "Share the app so they can view their agreement and complete their move-in details.",
      notifyTenantPromptTitle: "Let {name} know on WhatsApp",
      notifyTenantPromptBody: "Share this so they can open the app and complete their move-in details.",
      shareOnWhatsappBtn: "💬 Share on WhatsApp",
      installAppWhatsappMessage: "Hi, this is {owner} from ToLetBook. I've added you as a tenant for \"{property}\". Please install the app to view your rent, agreement and complete your move-in details: {link}",
      notifyTenantWhatsappMessage: "Hi, this is {owner} from ToLetBook. I've added you as a tenant for \"{property}\". Open the app to view your rent, agreement and complete your move-in details: {link}",
      editButton: "Edit",
      deleteButton: "Delete",
      perBed: "{amount}/bed",
      priceTiers: "{count} price tiers",
      occupiedFraction: "{occupied}/{total} occupied",
      occupiedBadge: "Occupied",
      vacantBadge: "Vacant",
      rentalAgreementButton: "Rental Agreement",
      analyticsButton: "📊 View Analytics",
      rentalReceiptsButton: "Rental Receipts",
      noReceiptsYet: "No paid receipts yet.",
      receiptsBackLink: "‹ Back",
      loadingReceipts: "Loading…",
      housesOnToLetSummary: "{listed} of {total} houses on TO-LET",
      toLetLimitTitle: "TO-LET limit reached",
      toLetLimitBody: "Your Basic plan allows only 1 house per property on the TO-LET marketplace at a time. Upgrade to Pro for unlimited listings, or claim a free month of Pro through Refer & Earn.",
      toLetLimitUpgradeBtn: "Upgrade to Pro",
      toLetLimitReferBtn: "Refer & Earn a free month",
    },
    assignTenantForm: {
      contactPickerUnsupported: "Contact picker isn't supported on this device/browser",
      errNameRequired: "Tenant name is required",
      errPhoneRequired: "Enter a valid 10-digit phone number",
      newTenant: "New tenant",
      existingTenant: "Existing tenant",
      addFromContacts: "Add from contacts",
      emptyExistingTenants: "No other tenants on your properties yet.",
      noPhoneOnFile: "No phone on file",
      tenantNameLabel: "Tenant name",
      phoneLabel: "Phone (matches their ToLetBook account, if registered)",
      phonePlaceholder: "10-digit number",
      aadhaarLabel: "Aadhaar (optional)",
      aadhaarPhotosLabel: "Aadhaar photos (optional)",
      aadhaarFront: "Front",
      aadhaarBack: "Back",
      removeAadhaarPhotoAria: "Remove photo",
      rentLabel: "Rent / month",
      depositLabel: "Deposit",
      dateOfOccupancyLabel: "Date of occupancy",
      dateHint: "If they already live here, pick when they actually moved in — not today.",
      rentDueDayLabel: "Rent due day",
      rentDueDayHint: "Day of the month rent is due (1–31). Defaults to the date of occupancy.",
      noticePeriodLabel: "Notice period (days)",
      noticePeriodHint: "How much notice the tenant must give before vacating. Defaults to 60 days (2 months).",
      addTenantButton: "Add tenant",
    },
    addPropertyForm: {
      errNameRequired: "Give the property a name",
      errLocalityRequired: "Enter the locality",
      errCityRequired: "Enter the city",
      errStateRequired: "Select a state",
      errPincodeRequired: "Enter the pincode",
      errFurnishingRequired: "Select the furnishing status",
      errCommunityTypeRequired: "Select a community type",
      errRentRequired: "Enter the expected rent",
      errDepositRequired: "Enter the expected deposit",
      upgradeNowBtn: "Upgrade now",
      heading: "Add Property",
      localityLabel: "Locality",
      localityPlaceholder: "e.g. Indiranagar, Bengaluru",
      cityLabel: "City",
      cityPlaceholder: "e.g. Bengaluru",
      stateLabel: "State",
      statePlaceholder: "Select state",
      pincodeLabel: "Pincode",
      pincodePlaceholder: "6-digit pincode",
      chipIndependent: "Independent",
      chipPgHostel: "PG / Hostel",
      chipCommercial: "Commercial",
      propertyNameLabel: "Property name",
      propertyNamePlaceholder: "e.g. 3, Rosewood Apartments",
      typeLabel: "Type",
      expectedRentLabel: "Expected rent",
      expectedDepositLabel: "Expected deposit",
      maintenanceLabel: "Maintenance / month (optional)",
      maintenancePlaceholder: "e.g. 1500",
      multiUnitChip: "This property has multiple houses",
      multiUnitHint: "For a compound or building with more than one standalone house/flat for rent — each gets its own rent, deposit, and tenant.",
      commercialDetailsHeading: "Commercial details",
      carpetAreaLabel: "Carpet area (sq ft)",
      builtUpAreaLabel: "Built-up area (sq ft)",
      lockInMonthsLabel: "Lock-in period (months)",
      rentEscalationPercentLabel: "Rent escalation (%)",
      rentEscalationYearsLabel: "Escalation every (years)",
      possessionStatusLabel: "Possession status",
      possession_ready: "Ready to move",
      possession_under_fitout: "Under fit-out",
      washroomTypeLabel: "Washroom",
      washroom_attached: "Attached",
      washroom_shared: "Shared",
      commercialAmenitiesLabel: "Amenities",
      powerBackupChip: "Power backup",
      powerLoadDetailsLabel: "Power backup details (optional)",
      powerLoadDetailsPlaceholder: "e.g. 3-phase, 100% DG backup",
      parkingSpotsLabel: "Reserved parking (cars/bikes)",
      gstChip: "18% GST applicable on rent",
      frontageChip: "Frontage / signage rights",
      seatsCountLabel: "Seats",
      cabinsCountLabel: "Cabins",
      meetingRoomsCountLabel: "Meeting rooms",
      amenitiesLabel: "Amenities",
      amenity_wifi: "Wifi",
      amenity_parking: "Parking",
      amenity_lift: "Lift",
      amenity_power_backup: "Power backup",
      amenity_security: "Security",
      amenity_gym: "Gym",
      addAmenityPlaceholder: "Add another amenity",
      addAmenityBtn: "Add",
      housesHeading: "Houses",
      addHouseLink: "+ Add house ›",
      houseNameLabel: "House name",
      hasExistingTenantChip: "This property already has a tenant",
      tenantNameLabel: "Tenant name",
      phoneLabel: "Phone (matches their ToLetBook account, if registered)",
      phonePlaceholder: "10-digit number",
      aadhaarLabel: "Aadhaar (optional)",
      dateOfOccupancyLabel: "Date of occupancy",
      dateHint: "When they actually moved in — rent and deposit use the expected values above.",
      occupancyLabel: "Occupancy",
      occBoys: "Boys",
      occGirls: "Girls",
      occCoEd: "Co-ed",
      roomsHeading: "Rooms",
      addRoomLink: "+ Add room ›",
      sharingTypeLabel: "Sharing type",
      rentPerBedLabel: "Rent/bed",
      depositPerBedLabel: "Deposit/bed",
      bedsLabel: "Beds",
      mealPlanLabel: "Meals included",
      preferredTenantsLabel: "Preferred tenants",
      meal_breakfast: "Breakfast",
      meal_lunch: "Lunch",
      meal_dinner: "Dinner",
      tenantType_student: "Student",
      tenantType_professional: "Working professional",
      roomAc: "AC",
      roomAttachedBathroom: "Attached bathroom",
      addPropertyButton: "Add property",
      photosSkipHint: "Photos aren't required to list — but listings with photos get far more views from tenants.",
      addPropertyLaterButton: "Skip for now, add photos later",
    },
    photoNudge: {
      title: "Add photos to get more reach",
      body: "{property} has no photos yet — listings with photos get far more views from tenants.",
      addPhotosButton: "Add photos now",
      remindLaterButton: "Remind me later",
    },
    homePromo: {
      estampTitleHighlight: "Rental Agreement",
      estampTitle: "e-stamping is coming soon",
      estampSub: "Government-verified and fully e-stamped, right in the app.",
      estampCta: "Notify me",
      estampCtaDone: "On the list ✓",
      estampToast: "Thanks! We'll notify you when e-stamping is live.",
      referTitle: "Give 1 month, Get 1 month",
      referSub: "Invite a friend to ToLetBook — you both earn a free month of Pro.",
      referCta: "Invite & earn",
    },
    ownerClaims: {
      title: "Needs your confirmation",
      confirmBtn: "Confirm received",
      rejectBtn: "Reject",
      reasonLabel: "Reason (optional, the tenant will see it)",
      reasonPlaceholder: "e.g. Not in my account yet",
      sendReject: "Reject payment",
      confirmedToast: "Payment confirmed",
      rejectedToast: "Payment rejected, tenant notified",
      modeUpi: "Paid via UPI",
      modeBank: "Paid via bank transfer",
      modeCash: "Paid in cash",
      refLine: "Ref {ref}",
      noRef: "no reference given",
    },
    ownerLedgers: {
      electricityBtn: "Bill electricity",
      electricityRoomBtn: "Bill a room's electricity",
      electricityTitle: "Electricity bill",
      electricityRoomLabel: "Room",
      electricityRoomOption: "Room {n} · {type} ({count} living)",
      electricityMonthLabel: "Add to which month's bill",
      electricityAmountLabel: "Total amount (₹)",
      electricityAmountSplitHint: "Split equally between everyone living in the room.",
      electricityNoteLabel: "Note (optional)",
      electricitySubmit: "Add to bills",
      electricityAdded: "Added to {n} bill(s)",
      electricitySkipped: "{n} already paid, skipped",
      electricityInvalid: "Enter an amount and choose a room.",
      chargeRemoved: "Charge removed",
      lateFeeHeading: "Late fee",
      lateFeeOff: "Off — no late fee",
      lateFeeSummary: "₹{amount} if rent is unpaid {days} days after the due date",
      lateFeeEdit: "Edit",
      lateFeeAmountLabel: "Late fee (₹, 0 = off)",
      lateFeeGraceLabel: "Grace days after due date",
      lateFeeSave: "Save",
      lateFeeSaved: "Late fee saved",
      justNow: "just now",
      minsAgo: "{mins}m ago",
      hoursAgo: "{hours}h ago",
      daysAgo: "{days}d ago",
      emptyNoProperty: "Add a property first to track its notebook.",
      heading: "Notebook",
      collectedLabel: "collected",
      pendingLabel: "pending",
      emptyNoTenants: "No tenants here yet.",
      markedPaid: "Marked as paid",
      reminderSent: "Reminder sent to the tenant",
      emptyNoActiveTenants: "No active tenants here yet.",
      otherCategory: "Other",
      pastTenantsHeading: "Past tenants · {count}",
      rentLedgerLabel: "Rent ledger · last 12 months",
      paidFraction: "{paid}/{total} paid",
      entriesLabel: "Entries",
      rentPaid: "Rent paid",
      rentDue: "Rent due",
      verifiedTenantBadge: "Verified",
      depositLabel: "Security deposit",
      depositPaidNote: "Paid via {mode} on {date}",
      depositModeOnline: "UPI/bank transfer",
      depositModeCash: "cash",
      awaitingConfirmation: "Awaiting your confirmation",
      tenantClaimedPaid: "Tenant marked this as paid via {mode} — confirm once you've received it.",
      confirmReceipt: "Confirm receipt",
      markReceived: "Mark received (cash)",
      markingBusy: "Marking…",
      remindAgain: "🔔 Remind again",
      waRemindBtn: "💬 Remind on WhatsApp",
      waRemindMessage: "Hi {tenant}, a gentle reminder that your rent of {amount} for {month} is still pending. Please pay when you can. Thank you. - {owner}",
      waRemindUpi: "You can pay by UPI to {upi}.",
      remind: "🔔 Remind",
      sendingBusy: "Sending…",
      remindedAgo: "Reminded {time}",
      closeButton: "‹ Close",
      refundLabel: "Refund",
      refundedAmount: "{amount} refunded",
    },
    ownerReports: {
      plHeading: "Profit & loss",
      plSub: "Last 6 months, counted from payments marked received.",
      plIncome: "Income",
      plExpenses: "Expenses",
      plNet: "Net",
      plRent: "Rent",
      plElectricity: "Electricity",
      plLateFees: "Late fees",
      plOther: "Other charges",
      expHeading: "Expenses",
      expAdd: "Add expense",
      expCategory: "Category",
      expAmount: "Amount (₹)",
      expDate: "Date",
      expNote: "Note (optional)",
      expProperty: "Property",
      expAllProperties: "All properties",
      expSave: "Save expense",
      expEmpty: "No expenses recorded yet.",
      expDelete: "Delete",
      expSaved: "Expense added",
      expDeleted: "Expense removed",
      expInvalid: "Enter an amount and a date.",
      catRepairs: "Repairs",
      catSalary: "Staff salary",
      catElectricity: "Electricity",
      catWater: "Water",
      catInternet: "Internet",
      catFood: "Food",
      catCleaning: "Cleaning",
      catTax: "Taxes",
      catOther: "Other",
      emptyState: "No ledger activity yet — add a tenant to start collecting.",
      heading: "Reports",
      collected: "Collected",
      pending: "Pending",
      paidOnRecord: "Paid on record",
      collectionsByMonth: "Collections by month",
      monthTooltip: "{month}: {collected} collected, {pending} pending",
      byProperty: "By property",
      pendingAmount: "{amount} pending",
      exportCsvButton: "Export ledger as CSV",
      hint: "Every row traces back to a ledger entry — no figures added here.",
    },
    ownerBroadcast: {
      title: "WhatsApp Broadcast",
      messageLabel: "Message",
      messagePlaceholder: "e.g. Reminder: society water tank cleaning on Saturday, water supply will be off 10am–1pm.",
      messageHint: "Opens WhatsApp with this message pre-filled for each tenant you send it to — you still tap send yourself in WhatsApp.",
      emptyState: "No active tenants to message yet.",
      recipientsHeading: "{count} active tenants",
      sendBtn: "Send",
      sentBtn: "Sent ✓",
    },
    ownerRequests: {
      vacateTab: "Vacate",
      visitsTab: "Visits",
      maintenanceTab: "Repairs",
      visitRequestsHeading: "Visit Requests",
      newCount: "{count} new",
      emptyNoVisits: "No visit requests yet.",
      maintenanceRequestsHeading: "Maintenance Requests",
      emptyNoRequests: "No requests raised yet.",
      noDetailsProvided: "No details provided",
      startWork: "Start work",
      markResolved: "Mark resolved",
      requestUpdated: "Request updated",
      visitConfirmed: "Visit confirmed",
      visitDeclined: "Visit declined",
      noPhoneOnFile: "No phone on file",
      verifiedTenantBadge: "Verified",
      visitLine: "{property} · {date} at {time}",
      declineButton: "Decline",
      confirmButton: "Confirm",
      statusOpen: "Open",
      statusInProgress: "In progress",
      statusResolved: "Resolved",
      visitStatusPending: "Pending",
      visitStatusConfirmed: "Confirmed",
      visitStatusDeclined: "Declined",
      visitStatusCancelled: "Cancelled",
      vacateRequestsHeading: "Vacate Notices",
      emptyNoVacates: "No vacate notices yet.",
      vacateLine: "{property} · vacating {date}",
      noticeGivenLine: "Notice given on {date}",
      acknowledgeButton: "Acknowledge",
      vacateAcknowledged: "Vacate notice acknowledged",
      vacateStatusPending: "Pending",
      vacateStatusAcknowledged: "Acknowledged",
      vacateStatusCancelled: "Cancelled",
    },
    editPropertyForm: {
      errNameRequired: "Give the property a name",
      propertyUpdated: "Property updated",
      couldNotUpdate: "Could not update property",
      localityLabel: "Locality",
      propertyNameLabel: "Property name",
      typeLabel: "Type",
      residentialGroup: "Residential",
      commercialGroup: "Commercial",
      expectedRentLabel: "Expected rent",
      rentBenchmarkHint: "Similar listings nearby average {amount}/mo",
      expectedDepositLabel: "Expected deposit",
      maintenanceLabel: "Maintenance / month (optional)",
      occupancyLabel: "Occupancy",
      occBoys: "Boys",
      occGirls: "Girls",
      occCoEd: "Co-ed",
      facilitiesLabel: "Facilities",
      facility_wifi: "Wi-Fi",
      facility_meals: "Meals",
      facility_laundry: "Laundry",
      facility_powerBackup: "Power Backup",
      facility_cctv: "CCTV",
      facility_parking: "Parking",
      facility_ac: "AC",
      facility_tv: "TV",
      facility_washingMachine: "Washing Machine",
      perHouseFurnishingHint: "Furnishing is set per house — open each house below to set it.",
      furnishingStatusLabel: "Furnishing",
      furnishing_unfurnished: "Unfurnished",
      furnishing_semi_furnished: "Semi-furnished",
      furnishing_fully_furnished: "Fully furnished",
      commercialFurnishing_unfurnished: "Bare shell",
      commercialFurnishing_semi_furnished: "Warm shell",
      commercialFurnishing_fully_furnished: "Fully furnished",
      communityTypeLabel: "Community type",
      community_gated: "Gated community",
      community_semi_gated: "Semi gated community",
      community_villa: "Villa",
      community_apartment: "Apartment",
      community_independent: "Independent",
      floorNumberLabel: "Floor",
      floorNumberPlaceholder: "e.g. 3 or Ground",
      floorChipLabel: "Floor {floor}",
      furnishingItemsLabel: "Furniture & appliances",
      furnishingItemsHint: "Enter how many of each the tenant will have.",
      item_fan: "Fans",
      item_light: "Lights",
      item_ac: "AC",
      item_fridge: "Fridge",
      item_washingMachine: "Washing machine",
      item_sofa: "Sofa",
      item_bed: "Bed",
      item_heater: "Water heater (bathroom)",
      item_chimney: "Chimney (kitchen)",
      addFurnitureItemPlaceholder: "+ Add furniture / appliance",
      customItemNamePlaceholder: "e.g. Study table",
      addCustomItemBtn: "+ Add another item",
    },
    endTenancyForm: {
      tenancyEnded: "Tenancy ended — refund details sent to the tenant",
      couldNotEnd: "Could not end tenancy",
      heading: "End tenancy — {name}",
      dateOfExitLabel: "Date of exit",
      depositPaidLabel: "Deposit paid",
      deductionsLabel: "Deductions (damages, dues, etc.)",
      deductionNoteLabel: "Deduction note (optional)",
      deductionNotePlaceholder: "e.g. Wall paint touch-up",
      refundableDepositLabel: "Refundable deposit",
      confirmButton: "Confirm & End Tenancy",
      endingBusy: "Ending…",
    },
    propertyPhotos: {
      uploadFailed: "Upload failed",
      photoRemoved: "Photo removed",
      couldNotRemove: "Could not remove photo",
      removePhotoAria: "Remove photo",
    },
    photoStager: {
      photosLabel: "Photos (optional)",
      couldNotRead: "Could not read that image",
      removePhotoAria: "Remove photo",
    },
    photoPickerButtons: {
      addPhotoTitle: "Add a photo",
      takePhoto: "📷 Take photo",
      chooseFromFiles: "🖼 Choose from files",
    },
    ownerPlans: {
      title: "Plans",
      currentPlanLabel: "Current plan",
      trialBadge: "Free Premium trial",
      trialEndsOn: "Trial ends {date}",
      renewsOn: "Renews {date}",
      listingsUsed: "{used} of {cap} listings used",
      billingMonthly: "Monthly",
      billingYearly: "Yearly · save ~16%",
      basicName: "Basic",
      proName: "Pro",
      premiumName: "Premium",
      mostPopular: "Most popular",
      powerOwners: "Power owners",
      basicSub: "For owners just getting started",
      proSub: "For owners with a few properties",
      premiumSub: "For owners managing several properties",
      featListing1: "1 active listing",
      featListing3: "Up to 3 active listings",
      featListingUnlimited: "Unlimited listings",
      featEnquiries: "Tenant enquiries and visit requests",
      featLedger: "Rent collection and digital ledger",
      featAgreement: "Self-serve rental agreement generator — Coming soon",
      featMarketplace: "Visible on TO-LET marketplace",
      featEverythingBasic: "Everything in Basic",
      featEverythingPro: "Everything in Pro",
      featMarketplaceSwitch: "TO-LET marketplace switch — go live anytime",
      featFeatured: "Featured placement in search",
      featPriority: "Priority in similar-property suggestions",
      featTopListing: "Top listing — pinned at the top of marketplace search",
      featFreeEstamp: "1 free government e-stamp every month",
      featPriorityAgreement: "Priority rental agreement service (legal-reviewed, fast turnaround) — Coming soon",
      featPriorityVerification: "Priority identity-verification review",
      featPrioritySupport: "Priority support",
      unlimitedLabel: "Unlimited",
      rowActiveListings: "Active listings",
      rowActiveListingsDesc: "How many properties you can have live at once",
      rowEnquiries: "Enquiries & visit requests",
      rowEnquiriesDesc: "Tenants can message you and book a viewing",
      rowLedger: "Rent ledger",
      rowLedgerDesc: "Track who's paid and who's pending, every month",
      rowMarketplace: "TO-LET marketplace",
      rowMarketplaceDesc: "Your vacant listings show up in tenant search",
      rowFeatured: "Featured search placement",
      rowFeaturedDesc: "Ranked above other listings in search results",
      rowTopListing: "Top listing placement",
      rowTopListingDesc: "Pinned above Featured, first thing tenants see",
      rowAutoReminders: "Auto rent reminders",
      rowAutoRemindersDesc: "Tenants get nudged automatically when rent is due",
      rowFreeEstamp: "Free e-stamp / month",
      rowFreeEstampDesc: "One government e-stamp agreement, no extra charge",
      rowAnalytics: "Listing analytics",
      rowAnalyticsDesc: "See views, enquiries, and unlocks per listing",
      ctaCurrent: "Current",
      ctaSwitch: "Switch",
      currentPlanBtn: "Current plan",
      switchToBtn: "Switch to {plan}",
      noChargeNote: "No payment required yet — plans are free to switch while billing is being set up.",
      switchedToast: "Switched to the {plan} plan",
      switchFailedToast: "Could not switch plans",
      trialActiveNote: "You're on a free Premium trial — every Premium feature is unlocked until it ends.",
      cancelPlanBtn: "Cancel plan",
      pausePlanBtn: "Pause plan",
      resumePlanBtn: "Resume plan",
      pausedBadge: "Paused — no charges until you resume",
      pausedToast: "Plan paused — resume anytime from here",
      resumedToast: "Plan resumed",
      securePaymentNote: "Payments are processed securely via Razorpay.",
      pgTitle: "PG plans",
      pgIntro: "PG owners pay by the number of beds they run, across all their PGs — not by property.",
      pgBedsUsed: "{used} of {cap} beds used",
      pgBedsFreeUsed: "{used} of {cap} beds used (free tier)",
      pgBedsUnlimited: "{used} beds · no limit until {date}",
      pgGraceNote: "You're on an earlier plan with no bed limit until {date}. Choose a bed plan before then to keep adding beds.",
      pgFreeNote: "Up to {n} beds are free.",
      pgTierPro: "Pro features",
      pgTierPremium: "Premium features",
      pgTierPremiumNote: "Adds one free e-stamp a month, analytics and top listing.",
      pgIncludes: "Every plan includes your marketplace listing, featured placement and automatic rent reminders.",
      pgSlabStarter: "Starter",
      pgSlabStandard: "Standard",
      pgSlabPlus: "Plus",
      pgSlabLarge: "Large",
      pgBedRange: "{min}–{max} beds",
      pgBedRangeOpen: "{min}+ beds",
      pgFits: "Fits your {n} beds",
      pgTooSmall: "Fewer beds than you run",
      pgUnavailable: "Coming soon",
      pgChoose: "Choose",
      pgNoPayments: "Payments aren't set up yet.",
    },
    boostListing: {
      ctaTitle: "Boost this listing",
      ctaSub: "Featured placement for {days} days",
      activeTitle: "Featured for {days} more days",
      activeSub: "Boost ends {date}",
      sheetTitle: "Boost this listing",
      placementLabel: "Placement",
      placementValue: "Featured in search",
      durationLabel: "Duration",
      durationValue: "{days} days",
      scopeLabel: "Applies to",
      scopeValue: "This listing only",
      oneTimeNote: "One-time payment — no subscription",
      payBtn: "Pay ₹{amount} & Boost",
      processingBtn: "Processing…",
      paymentDesc: "Listing boost — 7 days Featured",
      paymentFailedToast: "Payment failed — try again",
      paymentsNotSetUp: "Payments aren't set up yet",
      boostedToast: "Listing boosted — Featured for 7 days",
    },
    propertyAnalytics: {
      back: "‹ Back",
      title: "Listing Analytics",
      views: "Views",
      enquiries: "Enquiries",
      unlocks: "Unlocks",
      viewsByMonth: "Views by month",
      emptyHint: "No views logged yet — analytics fill in as tenants browse this listing.",
      lockedTitle: "See who's looking at this listing",
      lockedBody: "Views, enquiries, and unlocks for this listing are a Premium plan feature.",
      upgradeBtn: "Upgrade to Premium",
    },
  },
  hi: {
    ownerHome: {
      sectionTitlePg: "आपके पीजी",
      sectionTitleProperties: "आपकी प्रॉपर्टी",
      thisMonth: "इस महीने",
      pctCollected: "{pct}% वसूला गया",
      received: "मिला",
      pending: "बकाया",
      remindAll: "सभी को रिमाइंड करें",
      remindersSent: "{count} किराएदारों को रिमाइंडर भेजा गया",
      couldNotSendReminders: "रिमाइंडर भेजा नहीं जा सका",
      bedOccupancy: "बेड ऑक्युपेंसी",
      bedsFraction: "{filled}/{total} बेड",
      acrossPg: "कुल {count} पीजी {unit}",
      pgUnitSingular: "प्रॉपर्टी",
      pgUnitPlural: "प्रॉपर्टीज़",
      totalCount: "कुल {count}",
      addPropertyAria: "प्रॉपर्टी जोड़ें",
      addUnitFab: "यूनिट जोड़ें",
      sendWhatsappReceiptBtn: "WhatsApp रसीद भेजें",
      directUpiPayLinkBtn: "सीधा UPI पे लिंक",
      whatsappReceiptMessage: "नमस्ते {tenant}, {property} के लिए आपकी किराया रसीद: {amount} ({status})। - {owner}, ToLetBook के ज़रिए",
      upiPayLinkMessage: "नमस्ते {tenant}, कृपया {property} का इस महीने का किराया ({amount}) UPI से चुकाएँ: {link} - {owner}, ToLetBook के ज़रिए",
      noTenantPhone: "इस किराएदार का फोन नंबर दर्ज नहीं है",
      noUpiIdSet: "पहले सेटिंग्स में अपनी UPI ID डालें",
      tabProperty: "प्रॉपर्टी",
      tabPg: "पीजी",
      emptyNoProperties: "अभी कोई प्रॉपर्टी नहीं है। पहली प्रॉपर्टी जोड़ने के लिए + पर टैप करें।",
      emptyNoIndependent: "अभी कोई इंडिपेंडेंट प्रॉपर्टी नहीं है।",
      emptyNoPg: "अभी कोई पीजी प्रॉपर्टी नहीं है।",
      emptyNoCommercial: "अभी कोई कमर्शियल प्रॉपर्टी नहीं है।",
      filterAll: "सभी",
      filterResidential: "रेजिडेंशियल",
      filterCommercial: "कमर्शियल",
      filterPg: "PG / हॉस्टल",
      sqftSuffix: "वर्ग फुट",
      listedOnMarketplace: "मार्केटप्लेस पर लिस्ट कर दिया गया",
      removedFromMarketplace: "मार्केटप्लेस से हटा दिया गया",
      couldNotUpdateListing: "लिस्टिंग अपडेट नहीं हो सकी",
      rentUpdated: "किराया अपडेट हो गया",
      couldNotUpdateRent: "किराया अपडेट नहीं हो सका",
      maintenanceUpdated: "मेंटेनेंस अपडेट हो गया",
      couldNotUpdateMaintenance: "मेंटेनेंस अपडेट नहीं हो सका",
      paid: "भुगतान हुआ",
      tenantLabel: "किराएदार",
      perMonth: "/माह",
      phoneLabel: "फोन",
      noPhoneOnFile: "फोन नंबर दर्ज नहीं है",
      aadhaarLabel: "आधार",
      depositLabel: "डिपॉज़िट",
      moveInDateLabel: "मूव-इन तारीख",
      hideManageOptions: "मैनेज विकल्प छिपाएं",
      manage: "मैनेज करें",
      manageHouses: "घर मैनेज करें",
      rentalAgreement: "रेंटल एग्रीमेंट",
      rentalReceipts: "रेंटल रसीदें",
      modifyRent: "किराया बदलें",
      maintenanceMenu: "मेंटेनेंस",
      removeTenant: "किराएदार हटाएं",
      newRentLabel: "नया किराया / महीना",
      save: "सेव करें",
      maintenancePerMonthLabel: "मेंटेनेंस / महीना",
      vacantBadge: "खाली",
      expectedRentLabel: "अनुमानित किराया",
      depositSuffix: " · डिपॉज़िट {amount}",
      maintenanceLine: "+ {amount}/माह मेंटेनेंस",
      listOnToLet: "TO-LET मार्केटप्लेस पर लिस्ट करें",
      liveVisible: "लाइव · किराएदारों को दिखेगा",
      notListedYet: "अभी लिस्ट नहीं किया",
      toggleToLetAria: "TO-LET टॉगल करें",
      addTenantButton: "किराएदार जोड़ें",
      occBoys: "बॉयज़ पीजी",
      occGirls: "गर्ल्स पीजी",
      occCoEd: "को-एड पीजी",
      occDefault: "पीजी",
      listVacantBedsToLet: "खाली बेड TO-LET पर लिस्ट करें",
      fullyOccupied: "पूरी तरह भरा हुआ · कोई बेड खाली नहीं",
    },
    ownerListings: {
      propertyAdded: "प्रॉपर्टी जोड़ी गई",
      upgradeToastMsg: "आप अपने प्लान की लिस्टिंग सीमा तक पहुँच गए हैं — अपग्रेड विकल्पों पर भेजा जा रहा है।",
      heading: "मेरी प्रॉपर्टी",
      addLink: "+ जोड़ें ›",
      emptyState: "अभी कोई प्रॉपर्टी नहीं — अपनी पहली प्रॉपर्टी जोड़ें।",
      confirmDelete: '"{label}" को हटाएं? इसे वापस नहीं लाया जा सकता।',
      propertyDeleted: "प्रॉपर्टी हटा दी गई",
      confirmDeleteHouse: '"{label}" हटाएं? इसे वापस नहीं लाया जा सकता।',
      houseRemoved: "घर हटा दिया गया",
      houseUpdated: "घर की जानकारी अपडेट हुई",
      listedOnMarketplace: "मार्केटप्लेस पर लिस्ट कर दिया गया",
      removedFromMarketplace: "मार्केटप्लेस से हटा दिया गया",
      couldNotUpdateListing: "लिस्टिंग अपडेट नहीं हो सकी",
      vacantBedsFraction: "{vacant} / {total} खाली",
      listOnToLet: "TO-LET मार्केटप्लेस पर लिस्ट करें",
      listVacantBedsToLet: "खाली बेड TO-LET पर लिस्ट करें",
      liveVisible: "लाइव · किराएदारों को दिखेगा",
      notListedYet: "अभी लिस्ट नहीं किया",
      toggleToLetAria: "TO-LET टॉगल करें",
      moreOptionsAria: "और विकल्प",
      editDetails: "डिटेल्स एडिट करें",
      deleteProperty: "प्रॉपर्टी हटाएं",
      expectedRentLabel: "अनुमानित किराया",
      depositLabel: "डिपॉज़िट",
      noPhoneOnFile: "फोन नंबर दर्ज नहीं है",
      perMonth: "/माह",
      endTenancyButton: "टेनेंसी खत्म करें",
      editTenantButton: "किराएदार को मॉडिफाई करें",
      saveTenantButton: "बदलाव सेव करें",
      tenantAdded: "किराएदार जोड़ा गया",
      tenantUpdated: "किराएदार की जानकारी अपडेट हुई",
      roomAdded: "कमरा जोड़ा गया",
      couldNotAddRoom: "कमरा जोड़ा नहीं जा सका",
      addTenantLink: "+ किराएदार जोड़ें",
      installAppPromptTitle: "{name} ने अभी तक ToLetBook इंस्टॉल नहीं किया है",
      installAppPromptBody: "ऐप शेयर करें ताकि वे अपना एग्रीमेंट देख सकें और मूव-इन जानकारी पूरी कर सकें।",
      notifyTenantPromptTitle: "व्हाट्सएप पर {name} को बताएं",
      notifyTenantPromptBody: "यह शेयर करें ताकि वे ऐप खोलकर अपनी मूव-इन जानकारी पूरी कर सकें।",
      shareOnWhatsappBtn: "💬 WhatsApp पर शेयर करें",
      installAppWhatsappMessage: "नमस्ते, मैं ToLetBook से {owner} हूं। मैंने आपको \"{property}\" के लिए किराएदार के रूप में जोड़ा है। कृपया अपना किराया, एग्रीमेंट देखने और मूव-इन जानकारी पूरी करने के लिए ऐप इंस्टॉल करें: {link}",
      notifyTenantWhatsappMessage: "नमस्ते, मैं ToLetBook से {owner} हूं। मैंने आपको \"{property}\" के लिए किराएदार के रूप में जोड़ा है। कृपया अपना किराया, एग्रीमेंट देखने और मूव-इन जानकारी पूरी करने के लिए ऐप खोलें: {link}",
      editButton: "एडिट करें",
      deleteButton: "हटाएं",
      perBed: "{amount}/बेड",
      priceTiers: "{count} प्राइस टियर",
      occupiedFraction: "{occupied}/{total} भरे हुए",
      occupiedBadge: "भरा हुआ",
      vacantBadge: "खाली",
      rentalAgreementButton: "रेंटल एग्रीमेंट",
      rentalReceiptsButton: "किराया रसीदें",
      noReceiptsYet: "अभी तक कोई भुगतान की गई रसीद नहीं है।",
      receiptsBackLink: "‹ वापस",
      loadingReceipts: "लोड हो रहा है…",
    },
    assignTenantForm: {
      contactPickerUnsupported: "इस डिवाइस/ब्राउज़र पर कॉन्टैक्ट पिकर सपोर्ट नहीं करता",
      errNameRequired: "किराएदार का नाम ज़रूरी है",
      newTenant: "नया किराएदार",
      existingTenant: "मौजूदा किराएदार",
      addFromContacts: "कॉन्टैक्ट्स से जोड़ें",
      emptyExistingTenants: "आपकी प्रॉपर्टी पर अभी कोई और किराएदार नहीं है।",
      noPhoneOnFile: "फोन नंबर दर्ज नहीं है",
      tenantNameLabel: "किराएदार का नाम",
      phoneLabel: "फोन (अगर रजिस्टर्ड है, तो उनके ToLetBook अकाउंट से मैच होगा)",
      phonePlaceholder: "10 अंकों का नंबर",
      aadhaarLabel: "आधार (वैकल्पिक)",
      aadhaarPhotosLabel: "आधार फ़ोटो (वैकल्पिक)",
      aadhaarFront: "आगे",
      aadhaarBack: "पीछे",
      removeAadhaarPhotoAria: "फ़ोटो हटाएं",
      rentLabel: "किराया / महीना",
      depositLabel: "डिपॉज़िट",
      dateOfOccupancyLabel: "रहने की शुरुआत की तारीख",
      dateHint: "अगर वे पहले से यहां रहते हैं, तो वह तारीख चुनें जब वे असल में शिफ्ट हुए थे — आज की नहीं।",
      rentDueDayLabel: "किराया देने की तारीख",
      rentDueDayHint: "महीने की वह तारीख जब किराया देना है (1–31)। डिफ़ॉल्ट रहने की शुरुआत की तारीख होती है।",
      noticePeriodLabel: "नोटिस पीरियड (दिन)",
      noticePeriodHint: "किराएदार को खाली करने से पहले कितने दिन का नोटिस देना होगा। डिफ़ॉल्ट 60 दिन (2 महीने) है।",
      addTenantButton: "किराएदार जोड़ें",
    },
    addPropertyForm: {
      errNameRequired: "प्रॉपर्टी का नाम डालें",
      errLocalityRequired: "इलाका डालें",
      errCityRequired: "शहर डालें",
      errStateRequired: "राज्य चुनें",
      errPincodeRequired: "पिनकोड डालें",
      errFurnishingRequired: "फर्निशिंग स्थिति चुनें",
      errCommunityTypeRequired: "कम्युनिटी टाइप चुनें",
      errRentRequired: "अपेक्षित किराया डालें",
      errDepositRequired: "अपेक्षित डिपॉज़िट डालें",
      upgradeNowBtn: "अभी अपग्रेड करें",
      heading: "प्रॉपर्टी जोड़ें",
      localityLabel: "इलाका",
      localityPlaceholder: "जैसे इंदिरानगर, बेंगलुरु",
      cityLabel: "शहर",
      cityPlaceholder: "जैसे बेंगलुरु",
      stateLabel: "राज्य",
      statePlaceholder: "राज्य चुनें",
      pincodeLabel: "पिनकोड",
      pincodePlaceholder: "6 अंकों का पिनकोड",
      chipIndependent: "इंडिपेंडेंट",
      chipPgHostel: "पीजी / हॉस्टल",
      chipCommercial: "कमर्शियल",
      propertyNameLabel: "प्रॉपर्टी का नाम",
      propertyNamePlaceholder: "जैसे 3, रोज़वुड अपार्टमेंट्स",
      typeLabel: "टाइप",
      expectedRentLabel: "अनुमानित किराया",
      expectedDepositLabel: "अनुमानित डिपॉज़िट",
      maintenanceLabel: "मेंटेनेंस / महीना (वैकल्पिक)",
      maintenancePlaceholder: "जैसे 1500",
      multiUnitChip: "इस प्रॉपर्टी में कई घर हैं",
      multiUnitHint: "उस कंपाउंड या बिल्डिंग के लिए जिसमें किराए के लिए एक से ज़्यादा अलग घर/फ्लैट हैं — हर एक का किराया, डिपॉज़िट और किराएदार अलग होगा।",
      commercialDetailsHeading: "कमर्शियल जानकारी",
      carpetAreaLabel: "कारपेट एरिया (वर्ग फुट)",
      builtUpAreaLabel: "बिल्ट-अप एरिया (वर्ग फुट)",
      lockInMonthsLabel: "लॉक-इन अवधि (महीने)",
      rentEscalationPercentLabel: "किराया वृद्धि (%)",
      rentEscalationYearsLabel: "हर कितने साल में वृद्धि",
      possessionStatusLabel: "पज़ेशन स्थिति",
      possession_ready: "तुरंत उपलब्ध",
      possession_under_fitout: "फिट-आउट जारी है",
      washroomTypeLabel: "वॉशरूम",
      washroom_attached: "अटैच्ड",
      washroom_shared: "शेयर्ड",
      commercialAmenitiesLabel: "सुविधाएं",
      powerBackupChip: "पावर बैकअप",
      powerLoadDetailsLabel: "पावर बैकअप विवरण (वैकल्पिक)",
      powerLoadDetailsPlaceholder: "जैसे 3-फेज़, 100% DG बैकअप",
      parkingSpotsLabel: "रिज़र्व्ड पार्किंग (कार/बाइक)",
      gstChip: "किराए पर 18% GST लागू",
      frontageChip: "फ्रंटेज / साइनेज अधिकार",
      seatsCountLabel: "सीटें",
      cabinsCountLabel: "केबिन",
      meetingRoomsCountLabel: "मीटिंग रूम",
      housesHeading: "घर",
      addHouseLink: "+ घर जोड़ें ›",
      houseNameLabel: "घर का नाम",
      hasExistingTenantChip: "इस प्रॉपर्टी में पहले से किराएदार है",
      tenantNameLabel: "किराएदार का नाम",
      phoneLabel: "फोन (अगर रजिस्टर्ड है, तो उनके ToLetBook अकाउंट से मैच होगा)",
      phonePlaceholder: "10 अंकों का नंबर",
      aadhaarLabel: "आधार (वैकल्पिक)",
      dateOfOccupancyLabel: "रहने की शुरुआत की तारीख",
      dateHint: "जब वे असल में शिफ्ट हुए — किराया और डिपॉज़िट ऊपर दिए अनुमानित मान इस्तेमाल करेंगे।",
      occupancyLabel: "ऑक्युपेंसी",
      occBoys: "बॉयज़",
      occGirls: "गर्ल्स",
      occCoEd: "को-एड",
      roomsHeading: "कमरे",
      addRoomLink: "+ कमरा जोड़ें ›",
      sharingTypeLabel: "शेयरिंग टाइप",
      rentPerBedLabel: "किराया/बेड",
      depositPerBedLabel: "डिपॉज़िट/बेड",
      bedsLabel: "बेड",
      mealPlanLabel: "भोजन शामिल",
      preferredTenantsLabel: "पसंदीदा किराएदार",
      meal_breakfast: "नाश्ता",
      meal_lunch: "लंच",
      meal_dinner: "डिनर",
      tenantType_student: "स्टूडेंट",
      tenantType_professional: "वर्किंग प्रोफेशनल",
      roomAc: "एसी",
      roomAttachedBathroom: "अटैच्ड बाथरूम",
      addPropertyButton: "प्रॉपर्टी जोड़ें",
    },
    ownerLedgers: {
      justNow: "अभी अभी",
      minsAgo: "{mins} मिनट पहले",
      hoursAgo: "{hours} घंटे पहले",
      daysAgo: "{days} दिन पहले",
      emptyNoProperty: "नोटबुक ट्रैक करने के लिए पहले एक प्रॉपर्टी जोड़ें।",
      heading: "नोटबुक",
      emptyNoTenants: "यहां अभी कोई किराएदार नहीं है।",
      markedPaid: "भुगतान हुआ के रूप में मार्क किया गया",
      reminderSent: "किराएदार को रिमाइंडर भेज दिया गया",
      emptyNoActiveTenants: "यहां अभी कोई सक्रिय किराएदार नहीं है।",
      otherCategory: "अन्य",
      pastTenantsHeading: "पुराने किराएदार · {count}",
      rentLedgerLabel: "किराया लेजर · पिछले 12 महीने",
      paidFraction: "{paid}/{total} भुगतान हुआ",
      entriesLabel: "एंट्री",
      rentPaid: "किराया मिला",
      rentDue: "किराया बकाया",
      markReceived: "मिला हुआ मार्क करें (नकद)",
      markingBusy: "मार्क हो रहा है…",
      remindAgain: "🔔 फिर से रिमाइंड करें",
      remind: "🔔 रिमाइंड करें",
      sendingBusy: "भेजा जा रहा है…",
      remindedAgo: "{time} रिमाइंड किया",
      closeButton: "‹ बंद करें",
      refundLabel: "रिफंड",
      refundedAmount: "{amount} रिफंड किया गया",
    },
    ownerReports: {
      emptyState: "अभी लेजर में कोई गतिविधि नहीं — वसूली शुरू करने के लिए किराएदार जोड़ें।",
      heading: "रिपोर्ट्स",
      collected: "वसूला गया",
      pending: "बकाया",
      paidOnRecord: "रिकॉर्ड पर भुगतान",
      collectionsByMonth: "महीने के हिसाब से वसूली",
      monthTooltip: "{month}: {collected} वसूला गया, {pending} बकाया",
      byProperty: "प्रॉपर्टी के हिसाब से",
      pendingAmount: "{amount} बकाया",
      exportCsvButton: "लेजर को CSV के रूप में एक्सपोर्ट करें",
      hint: "हर पंक्ति लेजर एंट्री से जुड़ी है — यहां कोई अलग से आंकड़े नहीं जोड़े गए।",
    },
    ownerRequests: {
      visitRequestsHeading: "विज़िट रिक्वेस्ट",
      newCount: "{count} नई",
      emptyNoVisits: "अभी कोई विज़िट रिक्वेस्ट नहीं है।",
      maintenanceRequestsHeading: "मेंटेनेंस रिक्वेस्ट",
      emptyNoRequests: "अभी कोई रिक्वेस्ट नहीं की गई।",
      noDetailsProvided: "कोई जानकारी नहीं दी गई",
      startWork: "काम शुरू करें",
      markResolved: "हल हुआ मार्क करें",
      requestUpdated: "रिक्वेस्ट अपडेट हो गई",
      visitConfirmed: "विज़िट कन्फर्म हो गई",
      visitDeclined: "विज़िट अस्वीकार कर दी गई",
      noPhoneOnFile: "फोन नंबर दर्ज नहीं है",
      visitLine: "{property} · {date} को {time} बजे",
      declineButton: "अस्वीकार करें",
      confirmButton: "कन्फर्म करें",
      statusOpen: "खुला",
      statusInProgress: "प्रगति पर",
      statusResolved: "हल हो गया",
      visitStatusPending: "लंबित",
      visitStatusConfirmed: "कन्फर्म",
      visitStatusDeclined: "अस्वीकृत",
      visitStatusCancelled: "रद्द",
      vacateRequestsHeading: "वेकेट नोटिस",
      emptyNoVacates: "अभी कोई वेकेट नोटिस नहीं है।",
      vacateLine: "{property} · {date} को खाली किया जाएगा",
      noticeGivenLine: "{date} को नोटिस दिया गया",
      acknowledgeButton: "स्वीकार करें",
      vacateAcknowledged: "वेकेट नोटिस स्वीकार कर दिया गया",
      vacateStatusPending: "लंबित",
      vacateStatusAcknowledged: "स्वीकृत",
      vacateStatusCancelled: "रद्द",
    },
    editPropertyForm: {
      errNameRequired: "प्रॉपर्टी का नाम डालें",
      propertyUpdated: "प्रॉपर्टी अपडेट हो गई",
      couldNotUpdate: "प्रॉपर्टी अपडेट नहीं हो सकी",
      localityLabel: "इलाका",
      propertyNameLabel: "प्रॉपर्टी का नाम",
      typeLabel: "टाइप",
      residentialGroup: "रेजिडेंशियल",
      commercialGroup: "कमर्शियल",
      expectedRentLabel: "अनुमानित किराया",
      expectedDepositLabel: "अनुमानित डिपॉज़िट",
      maintenanceLabel: "मेंटेनेंस / महीना (वैकल्पिक)",
      occupancyLabel: "ऑक्युपेंसी",
      occBoys: "बॉयज़",
      occGirls: "गर्ल्स",
      occCoEd: "को-एड",
      facilitiesLabel: "सुविधाएं",
      facility_wifi: "Wi-Fi",
      facility_meals: "भोजन",
      facility_laundry: "लॉन्ड्री",
      facility_powerBackup: "पावर बैकअप",
      facility_cctv: "सीसीटीवी",
      facility_parking: "पार्किंग",
      facility_ac: "एसी",
      facility_tv: "टीवी",
      facility_washingMachine: "वॉशिंग मशीन",
      perHouseFurnishingHint: "फर्निशिंग हर घर के हिसाब से सेट होती है — नीचे हर घर खोलकर सेट करें।",
      furnishingStatusLabel: "फर्निशिंग",
      furnishing_unfurnished: "अनफर्निश्ड",
      furnishing_semi_furnished: "सेमी-फर्निश्ड",
      furnishing_fully_furnished: "फुली फर्निश्ड",
      commercialFurnishing_unfurnished: "बेयर शेल",
      commercialFurnishing_semi_furnished: "वॉर्म शेल",
      commercialFurnishing_fully_furnished: "फुली फर्निश्ड",
      communityTypeLabel: "कम्युनिटी टाइप",
      community_gated: "गेटेड कम्युनिटी",
      community_semi_gated: "सेमी गेटेड कम्युनिटी",
      community_villa: "विला",
      community_apartment: "अपार्टमेंट",
      community_independent: "इंडिपेंडेंट",
      floorNumberLabel: "फ्लोर",
      floorNumberPlaceholder: "जैसे 3 या ग्राउंड",
      floorChipLabel: "फ्लोर {floor}",
      furnishingItemsLabel: "फर्नीचर और उपकरण",
      furnishingItemsHint: "बताएं कि किराएदार को हर चीज़ कितनी मिलेगी।",
      item_fan: "पंखे",
      item_light: "लाइट्स",
      item_ac: "एसी",
      item_fridge: "फ्रिज",
      item_washingMachine: "वॉशिंग मशीन",
      item_sofa: "सोफा",
      item_bed: "बेड",
      item_heater: "वॉटर हीटर (बाथरूम)",
      item_chimney: "चिमनी (किचन)",
      addFurnitureItemPlaceholder: "+ फर्नीचर / उपकरण जोड़ें",
      customItemNamePlaceholder: "जैसे स्टडी टेबल",
      addCustomItemBtn: "+ और आइटम जोड़ें",
    },
    endTenancyForm: {
      tenancyEnded: "टेनेंसी खत्म हो गई — रिफंड की जानकारी किराएदार को भेज दी गई",
      couldNotEnd: "टेनेंसी खत्म नहीं हो सकी",
      heading: "टेनेंसी खत्म करें — {name}",
      dateOfExitLabel: "छोड़ने की तारीख",
      depositPaidLabel: "जमा किया गया डिपॉज़िट",
      deductionsLabel: "कटौती (नुकसान, बकाया आदि)",
      deductionNoteLabel: "कटौती का नोट (वैकल्पिक)",
      deductionNotePlaceholder: "जैसे दीवार पर पेंट ठीक कराना",
      refundableDepositLabel: "वापस मिलने वाला डिपॉज़िट",
      confirmButton: "कन्फर्म करें और टेनेंसी खत्म करें",
      endingBusy: "खत्म हो रहा है…",
    },
    propertyPhotos: {
      uploadFailed: "अपलोड नहीं हो सका",
      photoRemoved: "फोटो हटा दी गई",
      couldNotRemove: "फोटो हटाई नहीं जा सकी",
      removePhotoAria: "फोटो हटाएं",
    },
    photoStager: {
      photosLabel: "फोटो (वैकल्पिक)",
      couldNotRead: "वह इमेज पढ़ी नहीं जा सकी",
      removePhotoAria: "फोटो हटाएं",
    },
    photoPickerButtons: {
      addPhotoTitle: "फोटो जोड़ें",
      takePhoto: "📷 फोटो लें",
      chooseFromFiles: "🖼 फाइलों से चुनें",
    },
    ownerPlans: {
      title: "प्लान",
      currentPlanLabel: "मौजूदा प्लान",
      trialBadge: "फ्री प्रीमियम ट्रायल",
      trialEndsOn: "ट्रायल {date} को खत्म होगा",
      renewsOn: "{date} को रिन्यू होगा",
      listingsUsed: "{cap} में से {used} लिस्टिंग इस्तेमाल हुईं",
      billingMonthly: "मासिक",
      billingYearly: "सालाना · ~16% की बचत",
      basicName: "बेसिक",
      proName: "प्रो",
      premiumName: "प्रीमियम",
      mostPopular: "सबसे लोकप्रिय",
      powerOwners: "पावर ओनर्स",
      basicSub: "अभी शुरुआत कर रहे मालिकों के लिए",
      proSub: "कुछ प्रॉपर्टी वाले मालिकों के लिए",
      premiumSub: "कई प्रॉपर्टी मैनेज करने वाले मालिकों के लिए",
      featListing1: "1 एक्टिव लिस्टिंग",
      featListing3: "3 तक एक्टिव लिस्टिंग",
      featListingUnlimited: "अनलिमिटेड लिस्टिंग",
      featEnquiries: "किराएदार पूछताछ और विज़िट रिक्वेस्ट",
      featLedger: "किराया वसूली और डिजिटल लेजर",
      featAgreement: "खुद बनाएं रेंटल एग्रीमेंट जनरेटर — जल्द आ रहा है",
      featMarketplace: "TO-LET मार्केटप्लेस पर दिखे",
      featEverythingBasic: "बेसिक की सभी सुविधाएं",
      featEverythingPro: "प्रो की सभी सुविधाएं",
      featMarketplaceSwitch: "TO-LET मार्केटप्लेस स्विच — कभी भी लाइव करें",
      featFeatured: "सर्च में फीचर्ड प्लेसमेंट",
      featPriority: "मिलती-जुलती प्रॉपर्टी के सुझावों में प्राथमिकता",
      featTopListing: "टॉप लिस्टिंग — मार्केटप्लेस सर्च में सबसे ऊपर पिन की गई",
      featPriorityAgreement: "प्राथमिकता रेंटल एग्रीमेंट सेवा (कानूनी रूप से जांचा हुआ, तेज़ टर्नअराउंड) — जल्द आ रहा है",
      featPriorityVerification: "प्राथमिकता पहचान-सत्यापन समीक्षा",
      featPrioritySupport: "प्राथमिकता सपोर्ट",
      currentPlanBtn: "मौजूदा प्लान",
      switchToBtn: "{plan} पर स्विच करें",
      noChargeNote: "अभी कोई भुगतान ज़रूरी नहीं — बिलिंग सेट होने तक प्लान बदलना मुफ्त है।",
      switchedToast: "{plan} प्लान पर स्विच किया गया",
      switchFailedToast: "प्लान स्विच नहीं हो सका",
      trialActiveNote: "आप फ्री प्रीमियम ट्रायल पर हैं — जब तक यह खत्म नहीं होता, हर प्रीमियम फीचर अनलॉक है।",
      cancelPlanBtn: "प्लान कैंसिल करें",
      securePaymentNote: "भुगतान Razorpay के ज़रिए सुरक्षित रूप से प्रोसेस किया जाता है।",
    },
  },
  te: {
    ownerHome: {
      sectionTitlePg: "మీ పీజీలు",
      sectionTitleProperties: "మీ ప్రాపర్టీలు",
      thisMonth: "ఈ నెల",
      pctCollected: "{pct}% వసూలు అయ్యింది",
      received: "అందింది",
      pending: "పెండింగ్",
      remindAll: "అందరికీ రిమైండ్ చేయండి",
      remindersSent: "{count} మంది అద్దెదారులకు రిమైండర్ పంపబడింది",
      couldNotSendReminders: "రిమైండర్లు పంపలేకపోయాము",
      bedOccupancy: "బెడ్ ఆక్యుపెన్సీ",
      bedsFraction: "{filled}/{total} బెడ్‌లు",
      acrossPg: "మొత్తం {count} పీజీ {unit}",
      pgUnitSingular: "ప్రాపర్టీ",
      pgUnitPlural: "ప్రాపర్టీలు",
      totalCount: "మొత్తం {count}",
      addPropertyAria: "ప్రాపర్టీ జోడించండి",
      addUnitFab: "యూనిట్ జోడించండి",
      sendWhatsappReceiptBtn: "WhatsApp రసీదు పంపండి",
      directUpiPayLinkBtn: "డైరెక్ట్ UPI పే లింక్",
      whatsappReceiptMessage: "నమస్తే {tenant}, {property} కోసం మీ అద్దె రసీదు: {amount} ({status}). - {owner}, ToLetBook ద్వారా",
      upiPayLinkMessage: "నమస్తే {tenant}, దయచేసి {property} కి ఈ నెల అద్దె ({amount}) UPI ద్వారా చెల్లించండి: {link} - {owner}, ToLetBook ద్వారా",
      noTenantPhone: "ఈ అద్దెదారు ఫోన్ నంబర్ నమోదు కాలేదు",
      noUpiIdSet: "ముందుగా సెట్టింగ్స్‌లో మీ UPI ID నమోదు చేయండి",
      tabProperty: "ప్రాపర్టీ",
      tabPg: "పీజీ",
      emptyNoProperties: "ఇంకా ప్రాపర్టీలు లేవు. మీ మొదటి ప్రాపర్టీ జోడించడానికి + తాకండి.",
      emptyNoIndependent: "ఇంకా ఇండిపెండెంట్ ప్రాపర్టీలు లేవు.",
      emptyNoPg: "ఇంకా పీజీ ప్రాపర్టీలు లేవు.",
      emptyNoCommercial: "ఇంకా కమర్షియల్ ప్రాపర్టీలు లేవు.",
      filterAll: "అన్నీ",
      filterResidential: "రెసిడెన్షియల్",
      filterCommercial: "కమర్షియల్",
      filterPg: "PG / హాస్టల్",
      sqftSuffix: "చ.అ.",
      listedOnMarketplace: "మార్కెట్‌ప్లేస్‌లో లిస్ట్ చేయబడింది",
      removedFromMarketplace: "మార్కెట్‌ప్లేస్ నుండి తీసివేయబడింది",
      couldNotUpdateListing: "లిస్టింగ్ అప్‌డేట్ కాలేదు",
      rentUpdated: "అద్దె అప్‌డేట్ అయ్యింది",
      couldNotUpdateRent: "అద్దె అప్‌డేట్ కాలేదు",
      maintenanceUpdated: "మెయింటెనెన్స్ అప్‌డేట్ అయ్యింది",
      couldNotUpdateMaintenance: "మెయింటెనెన్స్ అప్‌డేట్ కాలేదు",
      paid: "చెల్లించారు",
      tenantLabel: "అద్దెదారు",
      perMonth: "/నెల",
      phoneLabel: "ఫోన్",
      noPhoneOnFile: "ఫోన్ నంబర్ నమోదు కాలేదు",
      aadhaarLabel: "ఆధార్",
      depositLabel: "డిపాజిట్",
      moveInDateLabel: "మూవ్-ఇన్ తేదీ",
      hideManageOptions: "మేనేజ్ ఆప్షన్‌లు దాచండి",
      manage: "మేనేజ్ చేయండి",
      manageHouses: "ఇళ్లను మేనేజ్ చేయండి",
      rentalAgreement: "రెంటల్ అగ్రిమెంట్",
      rentalReceipts: "రెంటల్ రసీదులు",
      modifyRent: "అద్దె మార్చండి",
      maintenanceMenu: "మెయింటెనెన్స్",
      removeTenant: "అద్దెదారుని తీసివేయండి",
      newRentLabel: "కొత్త అద్దె / నెల",
      save: "సేవ్ చేయండి",
      maintenancePerMonthLabel: "మెయింటెనెన్స్ / నెల",
      vacantBadge: "ఖాళీ",
      expectedRentLabel: "అంచనా అద్దె",
      depositSuffix: " · డిపాజిట్ {amount}",
      maintenanceLine: "+ {amount}/నెల మెయింటెనెన్స్",
      listOnToLet: "TO-LET మార్కెట్‌ప్లేస్‌లో లిస్ట్ చేయండి",
      liveVisible: "లైవ్ · అద్దెదారులకు కనిపిస్తుంది",
      notListedYet: "ఇంకా లిస్ట్ చేయలేదు",
      toggleToLetAria: "TO-LET టోగుల్ చేయండి",
      addTenantButton: "అద్దెదారుని జోడించండి",
      occBoys: "బాయ్స్ పీజీ",
      occGirls: "గర్ల్స్ పీజీ",
      occCoEd: "కో-ఎడ్ పీజీ",
      occDefault: "పీజీ",
      listVacantBedsToLet: "ఖాళీ బెడ్‌లను TO-LET‌లో లిస్ట్ చేయండి",
      fullyOccupied: "పూర్తిగా నిండింది · ఖాళీ బెడ్‌లు లేవు",
    },
    ownerListings: {
      propertyAdded: "ప్రాపర్టీ జోడించబడింది",
      upgradeToastMsg: "మీరు మీ ప్లాన్ లిస్టింగ్ పరిమితిని చేరుకున్నారు — అప్‌గ్రేడ్ ఆప్షన్‌లకు మళ్లిస్తోంది.",
      heading: "నా ప్రాపర్టీలు",
      addLink: "+ జోడించండి ›",
      emptyState: "ఇంకా ప్రాపర్టీలు లేవు — మీ మొదటి దాన్ని జోడించండి.",
      confirmDelete: '"{label}" ని తొలగించాలా? దీన్ని వెనక్కి తీసుకోలేరు.',
      propertyDeleted: "ప్రాపర్టీ తొలగించబడింది",
      confirmDeleteHouse: '"{label}" ని తొలగించాలా? దీన్ని వెనక్కి తీసుకోలేరు.',
      houseRemoved: "ఇల్లు తొలగించబడింది",
      houseUpdated: "ఇంటి వివరాలు అప్‌డేట్ అయ్యాయి",
      listedOnMarketplace: "మార్కెట్‌ప్లేస్‌లో లిస్ట్ చేయబడింది",
      removedFromMarketplace: "మార్కెట్‌ప్లేస్ నుండి తీసివేయబడింది",
      couldNotUpdateListing: "లిస్టింగ్ అప్‌డేట్ కాలేదు",
      vacantBedsFraction: "{vacant} / {total} ఖాళీ",
      listOnToLet: "TO-LET మార్కెట్‌ప్లేస్‌లో లిస్ట్ చేయండి",
      listVacantBedsToLet: "ఖాళీ బెడ్‌లను TO-LET‌లో లిస్ట్ చేయండి",
      liveVisible: "లైవ్ · అద్దెదారులకు కనిపిస్తుంది",
      notListedYet: "ఇంకా లిస్ట్ చేయలేదు",
      toggleToLetAria: "TO-LET టోగుల్ చేయండి",
      moreOptionsAria: "మరిన్ని ఆప్షన్‌లు",
      editDetails: "వివరాలు ఎడిట్ చేయండి",
      deleteProperty: "ప్రాపర్టీని తొలగించండి",
      expectedRentLabel: "అంచనా అద్దె",
      depositLabel: "డిపాజిట్",
      noPhoneOnFile: "ఫోన్ నంబర్ నమోదు కాలేదు",
      perMonth: "/నెల",
      endTenancyButton: "టెనెన్సీ ముగించండి",
      editTenantButton: "అద్దెదారుని మాడిఫై చేయండి",
      saveTenantButton: "మార్పులను సేవ్ చేయండి",
      tenantAdded: "అద్దెదారు జోడించబడ్డారు",
      tenantUpdated: "అద్దెదారు వివరాలు అప్‌డేట్ అయ్యాయి",
      roomAdded: "గది జోడించబడింది",
      couldNotAddRoom: "గదిని జోడించలేకపోయాము",
      addTenantLink: "+ అద్దెదారుని జోడించండి",
      installAppPromptTitle: "{name} ఇంకా ToLetBook ఇన్‌స్టాల్ చేయలేదు",
      installAppPromptBody: "వారు తమ అగ్రిమెంట్ చూడటానికి మరియు మూవ్-ఇన్ వివరాలు పూర్తి చేయడానికి యాప్‌ని షేర్ చేయండి.",
      notifyTenantPromptTitle: "WhatsApp‌లో {name}కి తెలియజేయండి",
      notifyTenantPromptBody: "వారు యాప్‌ని తెరిచి మూవ్-ఇన్ వివరాలు పూర్తి చేయడానికి దీన్ని షేర్ చేయండి.",
      shareOnWhatsappBtn: "💬 WhatsApp‌లో షేర్ చేయండి",
      installAppWhatsappMessage: "నమస్తే, నేను ToLetBook నుండి {owner}ని. నేను మిమ్మల్ని \"{property}\" కోసం అద్దెదారుగా జోడించాను. దయచేసి మీ అద్దె, అగ్రిమెంట్ చూడటానికి మరియు మూవ్-ఇన్ వివరాలు పూర్తి చేయడానికి యాప్‌ని ఇన్‌స్టాల్ చేయండి: {link}",
      notifyTenantWhatsappMessage: "నమస్తే, నేను ToLetBook నుండి {owner}ని. నేను మిమ్మల్ని \"{property}\" కోసం అద్దెదారుగా జోడించాను. దయచేసి మీ అద్దె, అగ్రిమెంట్ చూడటానికి మరియు మూవ్-ఇన్ వివరాలు పూర్తి చేయడానికి యాప్‌ని తెరవండి: {link}",
      editButton: "ఎడిట్ చేయండి",
      deleteButton: "తొలగించండి",
      perBed: "{amount}/బెడ్",
      priceTiers: "{count} ధర శ్రేణులు",
      occupiedFraction: "{occupied}/{total} నిండాయి",
      occupiedBadge: "నిండింది",
      vacantBadge: "ఖాళీ",
      rentalAgreementButton: "రెంటల్ అగ్రిమెంట్",
      rentalReceiptsButton: "అద్దె రసీదులు",
      noReceiptsYet: "ఇంకా చెల్లించిన రసీదులు లేవు.",
      receiptsBackLink: "‹ వెనుకకు",
      loadingReceipts: "లోడ్ అవుతోంది…",
    },
    assignTenantForm: {
      contactPickerUnsupported: "ఈ డివైస్/బ్రౌజర్‌లో కాంటాక్ట్ పికర్ సపోర్ట్ చేయదు",
      errNameRequired: "అద్దెదారు పేరు అవసరం",
      newTenant: "కొత్త అద్దెదారు",
      existingTenant: "ఇప్పటికే ఉన్న అద్దెదారు",
      addFromContacts: "కాంటాక్ట్‌ల నుండి జోడించండి",
      emptyExistingTenants: "మీ ప్రాపర్టీలలో ఇంకా వేరే అద్దెదారులు లేరు.",
      noPhoneOnFile: "ఫోన్ నంబర్ నమోదు కాలేదు",
      tenantNameLabel: "అద్దెదారు పేరు",
      phoneLabel: "ఫోన్ (వారు రిజిస్టర్ చేసుకుంటే, వారి ToLetBook ఖాతాతో సరిపోతుంది)",
      phonePlaceholder: "10 అంకెల నంబర్",
      aadhaarLabel: "ఆధార్ (ఐచ్ఛికం)",
      aadhaarPhotosLabel: "ఆధార్ ఫోటోలు (ఐచ్ఛికం)",
      aadhaarFront: "ముందు",
      aadhaarBack: "వెనుక",
      removeAadhaarPhotoAria: "ఫోటో తీసివేయండి",
      rentLabel: "అద్దె / నెల",
      depositLabel: "డిపాజిట్",
      dateOfOccupancyLabel: "నివాసం మొదలైన తేదీ",
      dateHint: "వారు ఇప్పటికే ఇక్కడ ఉంటుంటే, వారు నిజంగా మారిన తేదీని ఎంచుకోండి — ఈరోజుది కాదు.",
      rentDueDayLabel: "అద్దె గడువు తేదీ",
      rentDueDayHint: "నెలలో అద్దె చెల్లించాల్సిన రోజు (1–31). డిఫాల్ట్‌గా నివాసం మొదలైన తేదీ ఉంటుంది.",
      noticePeriodLabel: "నోటీసు వ్యవధి (రోజులు)",
      noticePeriodHint: "అద్దెదారు ఖాళీ చేయడానికి ముందు ఎంత నోటీసు ఇవ్వాలి. డిఫాల్ట్‌గా 60 రోజులు (2 నెలలు) ఉంటుంది.",
      addTenantButton: "అద్దెదారుని జోడించండి",
    },
    addPropertyForm: {
      errNameRequired: "ప్రాపర్టీకి పేరు పెట్టండి",
      errLocalityRequired: "లొకాలిటీ నమోదు చేయండి",
      errCityRequired: "నగరం నమోదు చేయండి",
      errStateRequired: "రాష్ట్రాన్ని ఎంచుకోండి",
      errPincodeRequired: "పిన్‌కోడ్ నమోదు చేయండి",
      errFurnishingRequired: "ఫర్నిషింగ్ స్థితిని ఎంచుకోండి",
      errCommunityTypeRequired: "కమ్యూనిటీ రకాన్ని ఎంచుకోండి",
      errRentRequired: "అంచనా అద్దెను నమోదు చేయండి",
      errDepositRequired: "అంచనా డిపాజిట్‌ను నమోదు చేయండి",
      upgradeNowBtn: "ఇప్పుడే అప్‌గ్రేడ్ చేయండి",
      heading: "ప్రాపర్టీ జోడించండి",
      localityLabel: "ప్రాంతం",
      localityPlaceholder: "ఉదా. ఇందిరానగర్, బెంగళూరు",
      cityLabel: "నగరం",
      cityPlaceholder: "ఉదా. బెంగళూరు",
      stateLabel: "రాష్ట్రం",
      statePlaceholder: "రాష్ట్రాన్ని ఎంచుకోండి",
      pincodeLabel: "పిన్‌కోడ్",
      pincodePlaceholder: "6 అంకెల పిన్‌కోడ్",
      chipIndependent: "ఇండిపెండెంట్",
      chipPgHostel: "పీజీ / హాస్టల్",
      chipCommercial: "కమర్షియల్",
      propertyNameLabel: "ప్రాపర్టీ పేరు",
      propertyNamePlaceholder: "ఉదా. 3, రోజ్‌వుడ్ అపార్ట్‌మెంట్స్",
      typeLabel: "టైప్",
      expectedRentLabel: "అంచనా అద్దె",
      expectedDepositLabel: "అంచనా డిపాజిట్",
      maintenanceLabel: "మెయింటెనెన్స్ / నెల (ఐచ్ఛికం)",
      maintenancePlaceholder: "ఉదా. 1500",
      multiUnitChip: "ఈ ప్రాపర్టీలో బహుళ ఇళ్లు ఉన్నాయి",
      multiUnitHint: "అద్దెకు ఒకటి కంటే ఎక్కువ స్వతంత్ర ఇళ్లు/ఫ్లాట్‌లు ఉన్న కాంపౌండ్ లేదా బిల్డింగ్ కోసం — ప్రతిదానికి దాని స్వంత అద్దె, డిపాజిట్, అద్దెదారు ఉంటాయి.",
      commercialDetailsHeading: "కమర్షియల్ వివరాలు",
      carpetAreaLabel: "కార్పెట్ ఏరియా (చ.అ.)",
      builtUpAreaLabel: "బిల్ట్-అప్ ఏరియా (చ.అ.)",
      lockInMonthsLabel: "లాక్-ఇన్ వ్యవధి (నెలలు)",
      rentEscalationPercentLabel: "అద్దె పెరుగుదల (%)",
      rentEscalationYearsLabel: "ప్రతి ఎన్ని సంవత్సరాలకు పెరుగుదల",
      possessionStatusLabel: "పొజెషన్ స్థితి",
      possession_ready: "వెంటనే అందుబాటులో",
      possession_under_fitout: "ఫిట్-అవుట్ జరుగుతోంది",
      washroomTypeLabel: "వాష్‌రూమ్",
      washroom_attached: "అటాచ్డ్",
      washroom_shared: "షేర్డ్",
      commercialAmenitiesLabel: "సౌకర్యాలు",
      powerBackupChip: "పవర్ బ్యాకప్",
      powerLoadDetailsLabel: "పవర్ బ్యాకప్ వివరాలు (ఐచ్ఛికం)",
      powerLoadDetailsPlaceholder: "ఉదా. 3-ఫేజ్, 100% DG బ్యాకప్",
      parkingSpotsLabel: "రిజర్వ్డ్ పార్కింగ్ (కార్లు/బైక్‌లు)",
      gstChip: "అద్దెపై 18% GST వర్తిస్తుంది",
      frontageChip: "ఫ్రంటేజ్ / సైనేజ్ హక్కులు",
      seatsCountLabel: "సీట్లు",
      cabinsCountLabel: "క్యాబిన్‌లు",
      meetingRoomsCountLabel: "మీటింగ్ రూమ్‌లు",
      housesHeading: "ఇళ్లు",
      addHouseLink: "+ ఇల్లు జోడించండి ›",
      houseNameLabel: "ఇంటి పేరు",
      hasExistingTenantChip: "ఈ ప్రాపర్టీలో ఇప్పటికే అద్దెదారు ఉన్నారు",
      tenantNameLabel: "అద్దెదారు పేరు",
      phoneLabel: "ఫోన్ (వారు రిజిస్టర్ చేసుకుంటే, వారి ToLetBook ఖాతాతో సరిపోతుంది)",
      phonePlaceholder: "10 అంకెల నంబర్",
      aadhaarLabel: "ఆధార్ (ఐచ్ఛికం)",
      dateOfOccupancyLabel: "నివాసం మొదలైన తేదీ",
      dateHint: "వారు నిజంగా మారిన తేదీ — అద్దె మరియు డిపాజిట్ పైన ఇచ్చిన అంచనా విలువలను ఉపయోగిస్తాయి.",
      occupancyLabel: "ఆక్యుపెన్సీ",
      occBoys: "బాయ్స్",
      occGirls: "గర్ల్స్",
      occCoEd: "కో-ఎడ్",
      roomsHeading: "గదులు",
      addRoomLink: "+ గది జోడించండి ›",
      sharingTypeLabel: "షేరింగ్ టైప్",
      rentPerBedLabel: "అద్దె/బెడ్",
      depositPerBedLabel: "డిపాజిట్/బెడ్",
      bedsLabel: "బెడ్‌లు",
      mealPlanLabel: "భోజనం చేర్చబడింది",
      preferredTenantsLabel: "ప్రాధాన్య అద్దెదారులు",
      meal_breakfast: "బ్రేక్‌ఫాస్ట్",
      meal_lunch: "లంచ్",
      meal_dinner: "డిన్నర్",
      tenantType_student: "స్టూడెంట్",
      tenantType_professional: "వర్కింగ్ ప్రొఫెషనల్",
      roomAc: "ఏసీ",
      roomAttachedBathroom: "అటాచ్డ్ బాత్రూమ్",
      addPropertyButton: "ప్రాపర్టీ జోడించండి",
    },
    ownerLedgers: {
      justNow: "ఇప్పుడే",
      minsAgo: "{mins} నిమిషాల క్రితం",
      hoursAgo: "{hours} గంటల క్రితం",
      daysAgo: "{days} రోజుల క్రితం",
      emptyNoProperty: "నోట్‌బుక్ ట్రాక్ చేయడానికి ముందు ఒక ప్రాపర్టీ జోడించండి.",
      heading: "నోట్‌బుక్",
      emptyNoTenants: "ఇక్కడ ఇంకా అద్దెదారులు లేరు.",
      markedPaid: "చెల్లించినట్లు మార్క్ చేయబడింది",
      reminderSent: "అద్దెదారుకి రిమైండర్ పంపబడింది",
      emptyNoActiveTenants: "ఇక్కడ ఇంకా యాక్టివ్ అద్దెదారులు లేరు.",
      otherCategory: "ఇతర",
      pastTenantsHeading: "గత అద్దెదారులు · {count}",
      rentLedgerLabel: "అద్దె లెడ్జర్ · గత 12 నెలలు",
      paidFraction: "{paid}/{total} చెల్లించారు",
      entriesLabel: "ఎంట్రీలు",
      rentPaid: "అద్దె చెల్లించారు",
      rentDue: "అద్దె పెండింగ్",
      markReceived: "అందినట్లు మార్క్ చేయండి (నగదు)",
      markingBusy: "మార్క్ అవుతోంది…",
      remindAgain: "🔔 మళ్ళీ రిమైండ్ చేయండి",
      remind: "🔔 రిమైండ్ చేయండి",
      sendingBusy: "పంపుతోంది…",
      remindedAgo: "{time} రిమైండ్ చేశారు",
      closeButton: "‹ మూసివేయండి",
      refundLabel: "రిఫండ్",
      refundedAmount: "{amount} రిఫండ్ చేయబడింది",
    },
    ownerReports: {
      emptyState: "ఇంకా లెడ్జర్ యాక్టివిటీ లేదు — వసూలు మొదలుపెట్టడానికి అద్దెదారుని జోడించండి.",
      heading: "రిపోర్ట్‌లు",
      collected: "వసూలు అయ్యింది",
      pending: "పెండింగ్",
      paidOnRecord: "రికార్డులో చెల్లింపు",
      collectionsByMonth: "నెలవారీ వసూళ్లు",
      monthTooltip: "{month}: {collected} వసూలు, {pending} పెండింగ్",
      byProperty: "ప్రాపర్టీ వారీగా",
      pendingAmount: "{amount} పెండింగ్",
      exportCsvButton: "లెడ్జర్‌ని CSVగా ఎక్స్‌పోర్ట్ చేయండి",
      hint: "ప్రతి వరుస ఒక లెడ్జర్ ఎంట్రీ నుండి వచ్చినదే — ఇక్కడ ఎలాంటి అంకెలు అదనంగా చేర్చలేదు.",
    },
    ownerRequests: {
      visitRequestsHeading: "విజిట్ రిక్వెస్ట్‌లు",
      newCount: "{count} కొత్తవి",
      emptyNoVisits: "ఇంకా విజిట్ రిక్వెస్ట్‌లు లేవు.",
      maintenanceRequestsHeading: "మెయింటెనెన్స్ రిక్వెస్ట్‌లు",
      emptyNoRequests: "ఇంకా రిక్వెస్ట్‌లు పెట్టలేదు.",
      noDetailsProvided: "వివరాలు ఇవ్వలేదు",
      startWork: "పని ప్రారంభించండి",
      markResolved: "పరిష్కరించినట్లు మార్క్ చేయండి",
      requestUpdated: "రిక్వెస్ట్ అప్‌డేట్ అయ్యింది",
      visitConfirmed: "విజిట్ కన్ఫర్మ్ అయ్యింది",
      visitDeclined: "విజిట్ తిరస్కరించారు",
      noPhoneOnFile: "ఫోన్ నంబర్ నమోదు కాలేదు",
      visitLine: "{property} · {date}న {time}కి",
      declineButton: "తిరస్కరించండి",
      confirmButton: "కన్ఫర్మ్ చేయండి",
      statusOpen: "ఓపెన్",
      statusInProgress: "పురోగతిలో",
      statusResolved: "పరిష్కారమైంది",
      visitStatusPending: "పెండింగ్",
      visitStatusConfirmed: "కన్ఫర్మ్ అయ్యింది",
      visitStatusDeclined: "తిరస్కరించారు",
      visitStatusCancelled: "రద్దయ్యింది",
      vacateRequestsHeading: "వేకేట్ నోటీసులు",
      emptyNoVacates: "ఇంకా వేకేట్ నోటీసులు లేవు.",
      vacateLine: "{property} · {date}న ఖాళీ చేస్తారు",
      noticeGivenLine: "{date}న నోటీసు ఇచ్చారు",
      acknowledgeButton: "ఆమోదించండి",
      vacateAcknowledged: "వేకేట్ నోటీసు ఆమోదించబడింది",
      vacateStatusPending: "పెండింగ్",
      vacateStatusAcknowledged: "ఆమోదించబడింది",
      vacateStatusCancelled: "రద్దయ్యింది",
    },
    editPropertyForm: {
      errNameRequired: "ప్రాపర్టీకి పేరు పెట్టండి",
      propertyUpdated: "ప్రాపర్టీ అప్‌డేట్ అయ్యింది",
      couldNotUpdate: "ప్రాపర్టీ అప్‌డేట్ కాలేదు",
      localityLabel: "ప్రాంతం",
      propertyNameLabel: "ప్రాపర్టీ పేరు",
      typeLabel: "టైప్",
      residentialGroup: "రెసిడెన్షియల్",
      commercialGroup: "కమర్షియల్",
      expectedRentLabel: "అంచనా అద్దె",
      expectedDepositLabel: "అంచనా డిపాజిట్",
      maintenanceLabel: "మెయింటెనెన్స్ / నెల (ఐచ్ఛికం)",
      occupancyLabel: "ఆక్యుపెన్సీ",
      occBoys: "బాయ్స్",
      occGirls: "గర్ల్స్",
      occCoEd: "కో-ఎడ్",
      facilitiesLabel: "సౌకర్యాలు",
      facility_wifi: "వై-ఫై",
      facility_meals: "భోజనం",
      facility_laundry: "లాండ్రీ",
      facility_powerBackup: "పవర్ బ్యాకప్",
      facility_cctv: "సీసీటీవీ",
      facility_parking: "పార్కింగ్",
      facility_ac: "ఏసీ",
      facility_tv: "టీవీ",
      facility_washingMachine: "వాషింగ్ మెషిన్",
      perHouseFurnishingHint: "ఫర్నిషింగ్ ప్రతి ఇంటికి విడిగా సెట్ చేస్తారు — కింద ప్రతి ఇంటిని తెరిచి సెట్ చేయండి.",
      furnishingStatusLabel: "ఫర్నిషింగ్",
      furnishing_unfurnished: "అన్‌ఫర్నిష్డ్",
      furnishing_semi_furnished: "సెమీ-ఫర్నిష్డ్",
      furnishing_fully_furnished: "ఫుల్లీ ఫర్నిష్డ్",
      commercialFurnishing_unfurnished: "బేర్ షెల్",
      commercialFurnishing_semi_furnished: "వార్మ్ షెల్",
      commercialFurnishing_fully_furnished: "ఫుల్లీ ఫర్నిష్డ్",
      communityTypeLabel: "కమ్యూనిటీ టైప్",
      community_gated: "గేటెడ్ కమ్యూనిటీ",
      community_semi_gated: "సెమీ గేటెడ్ కమ్యూనిటీ",
      community_villa: "విల్లా",
      community_apartment: "అపార్ట్‌మెంట్",
      community_independent: "ఇండిపెండెంట్",
      floorNumberLabel: "ఫ్లోర్",
      floorNumberPlaceholder: "ఉదా. 3 లేదా గ్రౌండ్",
      floorChipLabel: "ఫ్లోర్ {floor}",
      furnishingItemsLabel: "ఫర్నిచర్ & ఉపకరణాలు",
      furnishingItemsHint: "ప్రతి దాంట్లో అద్దెదారుకు ఎన్ని ఉంటాయో నమోదు చేయండి.",
      item_fan: "ఫ్యాన్లు",
      item_light: "లైట్లు",
      item_ac: "ఏసీ",
      item_fridge: "ఫ్రిజ్",
      item_washingMachine: "వాషింగ్ మెషిన్",
      item_sofa: "సోఫా",
      item_bed: "బెడ్",
      item_heater: "వాటర్ హీటర్ (బాత్రూమ్)",
      item_chimney: "చిమ్నీ (కిచెన్)",
      addFurnitureItemPlaceholder: "+ ఫర్నిచర్ / ఉపకరణం జోడించండి",
      customItemNamePlaceholder: "ఉదా. స్టడీ టేబుల్",
      addCustomItemBtn: "+ మరో ఐటెమ్ జోడించండి",
    },
    endTenancyForm: {
      tenancyEnded: "టెనెన్సీ ముగిసింది — రిఫండ్ వివరాలు అద్దెదారుకి పంపబడ్డాయి",
      couldNotEnd: "టెనెన్సీ ముగించలేకపోయాము",
      heading: "టెనెన్సీ ముగించండి — {name}",
      dateOfExitLabel: "వెళ్లిపోయే తేదీ",
      depositPaidLabel: "చెల్లించిన డిపాజిట్",
      deductionsLabel: "కోతలు (డ్యామేజీలు, బకాయిలు మొదలైనవి)",
      deductionNoteLabel: "కోత నోట్ (ఐచ్ఛికం)",
      deductionNotePlaceholder: "ఉదా. గోడ పెయింట్ టచ్-అప్",
      refundableDepositLabel: "తిరిగి ఇచ్చే డిపాజిట్",
      confirmButton: "కన్ఫర్మ్ చేసి టెనెన్సీ ముగించండి",
      endingBusy: "ముగుస్తోంది…",
    },
    propertyPhotos: {
      uploadFailed: "అప్‌లోడ్ విఫలమైంది",
      photoRemoved: "ఫోటో తీసివేయబడింది",
      couldNotRemove: "ఫోటో తీసివేయలేకపోయాము",
      removePhotoAria: "ఫోటో తీసివేయండి",
    },
    photoStager: {
      photosLabel: "ఫోటోలు (ఐచ్ఛికం)",
      couldNotRead: "ఆ ఇమేజ్‌ని చదవలేకపోయాము",
      removePhotoAria: "ఫోటో తీసివేయండి",
    },
    photoPickerButtons: {
      addPhotoTitle: "ఫోటో జోడించండి",
      takePhoto: "📷 ఫోటో తీయండి",
      chooseFromFiles: "🖼 ఫైళ్ల నుండి ఎంచుకోండి",
    },
    ownerPlans: {
      title: "ప్లాన్‌లు",
      currentPlanLabel: "ప్రస్తుత ప్లాన్",
      trialBadge: "ఉచిత ప్రీమియం ట్రయల్",
      trialEndsOn: "ట్రయల్ {date}కి ముగుస్తుంది",
      renewsOn: "{date}కి రెన్యూ అవుతుంది",
      listingsUsed: "{cap}లో {used} లిస్టింగ్‌లు వాడారు",
      billingMonthly: "నెలవారీ",
      billingYearly: "సంవత్సరానికి · ~16% ఆదా",
      basicName: "బేసిక్",
      proName: "ప్రో",
      premiumName: "ప్రీమియం",
      mostPopular: "అత్యంత జనాదరణ పొందింది",
      powerOwners: "పవర్ ఓనర్స్",
      basicSub: "ఇప్పుడే మొదలుపెడుతున్న యజమానుల కోసం",
      proSub: "కొన్ని ప్రాపర్టీలు ఉన్న యజమానుల కోసం",
      premiumSub: "అనేక ప్రాపర్టీలు నిర్వహించే యజమానుల కోసం",
      featListing1: "1 యాక్టివ్ లిస్టింగ్",
      featListing3: "3 వరకు యాక్టివ్ లిస్టింగ్‌లు",
      featListingUnlimited: "అపరిమిత లిస్టింగ్‌లు",
      featEnquiries: "అద్దెదారు ఎంక్వైరీలు మరియు విజిట్ రిక్వెస్ట్‌లు",
      featLedger: "అద్దె వసూలు మరియు డిజిటల్ లెడ్జర్",
      featAgreement: "సెల్ఫ్-సర్వ్ రెంటల్ అగ్రిమెంట్ జనరేటర్ — త్వరలో వస్తోంది",
      featMarketplace: "TO-LET మార్కెట్‌ప్లేస్‌లో కనిపిస్తుంది",
      featEverythingBasic: "బేసిక్‌లోని అన్నీ",
      featEverythingPro: "ప్రోలోని అన్నీ",
      featMarketplaceSwitch: "TO-LET మార్కెట్‌ప్లేస్ స్విచ్ — ఎప్పుడైనా లైవ్ చేయండి",
      featFeatured: "సెర్చ్‌లో ఫీచర్డ్ ప్లేస్‌మెంట్",
      featPriority: "సారూప్య-ప్రాపర్టీ సూచనల్లో ప్రాధాన్యత",
      featTopListing: "టాప్ లిస్టింగ్ — మార్కెట్‌ప్లేస్ సెర్చ్‌లో పైన పిన్ చేయబడుతుంది",
      featPriorityAgreement: "ప్రాధాన్యత రెంటల్ అగ్రిమెంట్ సేవ (లీగల్-రివ్యూడ్, వేగవంతమైన టర్న్‌అరౌండ్) — త్వరలో వస్తోంది",
      featPriorityVerification: "ప్రాధాన్యత గుర్తింపు-ధృవీకరణ సమీక్ష",
      featPrioritySupport: "ప్రాధాన్యత సపోర్ట్",
      currentPlanBtn: "ప్రస్తుత ప్లాన్",
      switchToBtn: "{plan}కి స్విచ్ చేయండి",
      noChargeNote: "ఇంకా చెల్లింపు అవసరం లేదు — బిల్లింగ్ సెటప్ అయ్యే వరకు ప్లాన్‌లు మారడం ఉచితం.",
      switchedToast: "{plan} ప్లాన్‌కి మారారు",
      switchFailedToast: "ప్లాన్ మార్చలేకపోయాము",
      trialActiveNote: "మీరు ఉచిత ప్రీమియం ట్రయల్‌లో ఉన్నారు — అది ముగిసేవరకు ప్రతి ప్రీమియం ఫీచర్ అన్‌లాక్ చేయబడింది.",
      cancelPlanBtn: "ప్లాన్ రద్దు చేయండి",
      securePaymentNote: "చెల్లింపులు Razorpay ద్వారా సురక్షితంగా ప్రాసెస్ చేయబడతాయి.",
    },
  },
  ta: {
    ownerHome: {
      sectionTitlePg: "உங்கள் பிஜிகள்",
      sectionTitleProperties: "உங்கள் சொத்துகள்",
      thisMonth: "இந்த மாதம்",
      pctCollected: "{pct}% வசூலிக்கப்பட்டது",
      received: "பெறப்பட்டது",
      pending: "நிலுவை",
      remindAll: "அனைவருக்கும் நினைவூட்டு",
      remindersSent: "{count} குடியிருப்பாளர்களுக்கு நினைவூட்டல் அனுப்பப்பட்டது",
      couldNotSendReminders: "நினைவூட்டல்களை அனுப்ப முடியவில்லை",
      bedOccupancy: "படுக்கை நிரப்பு விகிதம்",
      bedsFraction: "{filled}/{total} படுக்கைகள்",
      acrossPg: "மொத்தம் {count} பிஜி {unit}",
      pgUnitSingular: "சொத்து",
      pgUnitPlural: "சொத்துகள்",
      totalCount: "மொத்தம் {count}",
      addPropertyAria: "சொத்து சேர்",
      addUnitFab: "யூனிட் சேர்",
      sendWhatsappReceiptBtn: "WhatsApp ரசீது அனுப்பு",
      directUpiPayLinkBtn: "நேரடி UPI பே லிங்க்",
      whatsappReceiptMessage: "வணக்கம் {tenant}, {property}க்கான உங்கள் வாடகை ரசீது: {amount} ({status}). - {owner}, ToLetBook மூலம்",
      upiPayLinkMessage: "வணக்கம் {tenant}, {property}க்கான இந்த மாத வாடகையை ({amount}) UPI மூலம் செலுத்தவும்: {link} - {owner}, ToLetBook மூலம்",
      noTenantPhone: "இந்த குடியிருப்பாளருக்கு தொலைபேசி எண் பதிவு செய்யப்படவில்லை",
      noUpiIdSet: "முதலில் அமைப்புகளில் உங்கள் UPI ID-ஐ சேர்க்கவும்",
      tabProperty: "சொத்து",
      tabPg: "பிஜி",
      emptyNoProperties: "இன்னும் சொத்துகள் இல்லை. உங்கள் முதல் சொத்தைச் சேர்க்க + ஐத் தட்டவும்.",
      emptyNoIndependent: "இன்னும் தனி சொத்துகள் இல்லை.",
      emptyNoPg: "இன்னும் பிஜி சொத்துகள் இல்லை.",
      emptyNoCommercial: "இன்னும் வணிக சொத்துகள் இல்லை.",
      filterAll: "அனைத்தும்",
      filterResidential: "குடியிருப்பு",
      filterCommercial: "வணிகம்",
      filterPg: "PG / விடுதி",
      sqftSuffix: "சதுர அடி",
      listedOnMarketplace: "மார்க்கெட்பிளேஸில் பட்டியலிடப்பட்டது",
      removedFromMarketplace: "மார்க்கெட்பிளேஸிலிருந்து அகற்றப்பட்டது",
      couldNotUpdateListing: "பட்டியலைப் புதுப்பிக்க முடியவில்லை",
      rentUpdated: "வாடகை புதுப்பிக்கப்பட்டது",
      couldNotUpdateRent: "வாடகையைப் புதுப்பிக்க முடியவில்லை",
      maintenanceUpdated: "பராமரிப்பு புதுப்பிக்கப்பட்டது",
      couldNotUpdateMaintenance: "பராமரிப்பைப் புதுப்பிக்க முடியவில்லை",
      paid: "செலுத்தப்பட்டது",
      tenantLabel: "குடியிருப்பாளர்",
      perMonth: "/மாதம்",
      phoneLabel: "தொலைபேசி",
      noPhoneOnFile: "தொலைபேசி எண் பதிவு செய்யப்படவில்லை",
      aadhaarLabel: "ஆதார்",
      depositLabel: "டெபாசிட்",
      moveInDateLabel: "குடியேறிய தேதி",
      hideManageOptions: "நிர்வாக விருப்பங்களை மறை",
      manage: "நிர்வகி",
      manageHouses: "வீடுகளை நிர்வகி",
      rentalAgreement: "வாடகை ஒப்பந்தம்",
      rentalReceipts: "வாடகை ரசீதுகள்",
      modifyRent: "வாடகையை மாற்று",
      maintenanceMenu: "பராமரிப்பு",
      removeTenant: "குடியிருப்பாளரை அகற்று",
      newRentLabel: "புதிய வாடகை / மாதம்",
      save: "சேமி",
      maintenancePerMonthLabel: "பராமரிப்பு / மாதம்",
      vacantBadge: "காலி",
      expectedRentLabel: "எதிர்பார்க்கும் வாடகை",
      depositSuffix: " · டெபாசிட் {amount}",
      maintenanceLine: "+ {amount}/மாதம் பராமரிப்பு",
      listOnToLet: "TO-LET மார்க்கெட்பிளேஸில் பட்டியலிடு",
      liveVisible: "லைவ் · வாடகைதாரர்களுக்குத் தெரியும்",
      notListedYet: "இன்னும் பட்டியலிடப்படவில்லை",
      toggleToLetAria: "TO-LET டாகிள் செய்",
      addTenantButton: "குடியிருப்பாளரைச் சேர்",
      occBoys: "பாய்ஸ் பிஜி",
      occGirls: "கேர்ல்ஸ் பிஜி",
      occCoEd: "கோ-எட் பிஜி",
      occDefault: "பிஜி",
      listVacantBedsToLet: "காலி படுக்கைகளை TO-LET இல் பட்டியலிடு",
      fullyOccupied: "முழுவதும் நிரம்பியது · காலி படுக்கைகள் இல்லை",
    },
    ownerListings: {
      propertyAdded: "சொத்து சேர்க்கப்பட்டது",
      upgradeToastMsg: "உங்கள் திட்டத்தின் பட்டியல் வரம்பை எட்டிவிட்டீர்கள் — அப்கிரேட் விருப்பங்களுக்கு திருப்பி விடப்படுகிறது.",
      heading: "எனது சொத்துகள்",
      addLink: "+ சேர் ›",
      emptyState: "இன்னும் சொத்துகள் இல்லை — உங்கள் முதல் சொத்தைச் சேர்க்கவும்.",
      confirmDelete: '"{label}" ஐ நீக்கவா? இதை மீட்டெடுக்க முடியாது.',
      propertyDeleted: "சொத்து நீக்கப்பட்டது",
      confirmDeleteHouse: '"{label}" ஐ அகற்றவா? இதை மீட்டெடுக்க முடியாது.',
      houseRemoved: "வீடு அகற்றப்பட்டது",
      houseUpdated: "வீட்டு விவரங்கள் புதுப்பிக்கப்பட்டன",
      listedOnMarketplace: "மார்க்கெட்பிளேஸில் பட்டியலிடப்பட்டது",
      removedFromMarketplace: "மார்க்கெட்பிளேஸிலிருந்து அகற்றப்பட்டது",
      couldNotUpdateListing: "பட்டியலைப் புதுப்பிக்க முடியவில்லை",
      vacantBedsFraction: "{vacant} / {total} காலி",
      listOnToLet: "TO-LET மார்க்கெட்பிளேஸில் பட்டியலிடு",
      listVacantBedsToLet: "காலி படுக்கைகளை TO-LET இல் பட்டியலிடு",
      liveVisible: "லைவ் · வாடகைதாரர்களுக்குத் தெரியும்",
      notListedYet: "இன்னும் பட்டியலிடப்படவில்லை",
      toggleToLetAria: "TO-LET டாகிள் செய்",
      moreOptionsAria: "மேலும் விருப்பங்கள்",
      editDetails: "விவரங்களைத் திருத்து",
      deleteProperty: "சொத்தை நீக்கு",
      expectedRentLabel: "எதிர்பார்க்கும் வாடகை",
      depositLabel: "டெபாசிட்",
      noPhoneOnFile: "தொலைபேசி எண் பதிவு செய்யப்படவில்லை",
      perMonth: "/மாதம்",
      endTenancyButton: "வாடகைத் தொடர்பை முடி",
      editTenantButton: "குடியிருப்பாளரை மாற்று",
      saveTenantButton: "மாற்றங்களைச் சேமி",
      tenantAdded: "குடியிருப்பாளர் சேர்க்கப்பட்டார்",
      tenantUpdated: "குடியிருப்பாளர் விவரங்கள் புதுப்பிக்கப்பட்டன",
      roomAdded: "அறை சேர்க்கப்பட்டது",
      couldNotAddRoom: "அறையைச் சேர்க்க முடியவில்லை",
      addTenantLink: "+ குடியிருப்பாளரைச் சேர்",
      installAppPromptTitle: "{name} இன்னும் ToLetBook ஐ இன்ஸ்டால் செய்யவில்லை",
      installAppPromptBody: "அவர்கள் தங்கள் ஒப்பந்தத்தைப் பார்க்கவும், குடியேற்ற விவரங்களை முடிக்கவும் ஆப்பைப் பகிரவும்.",
      notifyTenantPromptTitle: "WhatsApp-இல் {name}-க்கு தெரியப்படுத்துங்கள்",
      notifyTenantPromptBody: "அவர்கள் ஆப்பைத் திறந்து குடியேற்ற விவரங்களை முடிக்க இதைப் பகிரவும்.",
      shareOnWhatsappBtn: "💬 WhatsApp-இல் பகிர்",
      installAppWhatsappMessage: "வணக்கம், நான் ToLetBook-இலிருந்து {owner}. நான் உங்களை \"{property}\" க்கான குடியிருப்பாளராக சேர்த்துள்ளேன். உங்கள் வாடகை, ஒப்பந்தத்தைப் பார்க்கவும், குடியேற்ற விவரங்களை முடிக்கவும் தயவுசெய்து ஆப்பை இன்ஸ்டால் செய்யவும்: {link}",
      notifyTenantWhatsappMessage: "வணக்கம், நான் ToLetBook-இலிருந்து {owner}. நான் உங்களை \"{property}\" க்கான குடியிருப்பாளராக சேர்த்துள்ளேன். உங்கள் வாடகை, ஒப்பந்தத்தைப் பார்க்கவும், குடியேற்ற விவரங்களை முடிக்கவும் தயவுசெய்து ஆப்பைத் திறக்கவும்: {link}",
      editButton: "திருத்து",
      deleteButton: "நீக்கு",
      perBed: "{amount}/படுக்கை",
      priceTiers: "{count} விலை நிலைகள்",
      occupiedFraction: "{occupied}/{total} நிரம்பியது",
      occupiedBadge: "நிரம்பியது",
      vacantBadge: "காலி",
      rentalAgreementButton: "வாடகை ஒப்பந்தம்",
      rentalReceiptsButton: "வாடகை ரசீதுகள்",
      noReceiptsYet: "இதுவரை பணம் செலுத்திய ரசீதுகள் இல்லை.",
      receiptsBackLink: "‹ பின்",
      loadingReceipts: "ஏற்றுகிறது…",
    },
    assignTenantForm: {
      contactPickerUnsupported: "இந்த சாதனம்/உலாவியில் தொடர்பு தேர்வு ஆதரிக்கப்படவில்லை",
      errNameRequired: "குடியிருப்பாளர் பெயர் தேவை",
      newTenant: "புதிய குடியிருப்பாளர்",
      existingTenant: "ஏற்கனவே உள்ள குடியிருப்பாளர்",
      addFromContacts: "தொடர்புகளிலிருந்து சேர்",
      emptyExistingTenants: "உங்கள் சொத்துகளில் இன்னும் வேறு குடியிருப்பாளர்கள் இல்லை.",
      noPhoneOnFile: "தொலைபேசி எண் பதிவு செய்யப்படவில்லை",
      tenantNameLabel: "குடியிருப்பாளர் பெயர்",
      phoneLabel: "தொலைபேசி (பதிவு செய்திருந்தால், அவர்களின் ToLetBook கணக்குடன் பொருந்தும்)",
      phonePlaceholder: "10 இலக்க எண்",
      aadhaarLabel: "ஆதார் (விருப்பத்திற்குரியது)",
      aadhaarPhotosLabel: "ஆதார் புகைப்படங்கள் (விருப்பத்திற்குரியது)",
      aadhaarFront: "முன்பக்கம்",
      aadhaarBack: "பின்பக்கம்",
      removeAadhaarPhotoAria: "படத்தை அகற்று",
      rentLabel: "வாடகை / மாதம்",
      depositLabel: "டெபாசிட்",
      dateOfOccupancyLabel: "குடியிருப்பு தொடங்கிய தேதி",
      dateHint: "அவர்கள் ஏற்கனவே இங்கே வசித்திருந்தால், அவர்கள் உண்மையில் குடிபெயர்ந்த தேதியைத் தேர்ந்தெடுக்கவும் — இன்றைய தேதி அல்ல.",
      rentDueDayLabel: "வாடகை செலுத்த வேண்டிய தேதி",
      rentDueDayHint: "மாதத்தில் வாடகை செலுத்த வேண்டிய நாள் (1–31). இயல்பாக குடியிருப்பு தொடங்கிய தேதி இருக்கும்.",
      noticePeriodLabel: "அறிவிப்பு காலம் (நாட்கள்)",
      noticePeriodHint: "குடியிருப்பாளர் காலி செய்வதற்கு முன் எவ்வளவு அறிவிப்பு கொடுக்க வேண்டும். இயல்பாக 60 நாட்கள் (2 மாதங்கள்) இருக்கும்.",
      addTenantButton: "குடியிருப்பாளரைச் சேர்",
    },
    addPropertyForm: {
      errNameRequired: "சொத்திற்கு ஒரு பெயர் கொடுங்கள்",
      errLocalityRequired: "பகுதியை உள்ளிடவும்",
      errCityRequired: "நகரத்தை உள்ளிடவும்",
      errStateRequired: "மாநிலத்தைத் தேர்ந்தெடுக்கவும்",
      errPincodeRequired: "பின்கோடை உள்ளிடவும்",
      errFurnishingRequired: "தளபாட நிலையைத் தேர்ந்தெடுக்கவும்",
      errCommunityTypeRequired: "சமூக வகையைத் தேர்ந்தெடுக்கவும்",
      errRentRequired: "எதிர்பார்க்கும் வாடகையை உள்ளிடவும்",
      errDepositRequired: "எதிர்பார்க்கும் டெபாசிட்டை உள்ளிடவும்",
      upgradeNowBtn: "இப்போது அப்கிரேட் செய்யவும்",
      heading: "சொத்தைச் சேர்",
      localityLabel: "பகுதி",
      localityPlaceholder: "எ.கா. இந்திரா நகர், பெங்களூரு",
      cityLabel: "நகரம்",
      cityPlaceholder: "எ.கா. பெங்களூரு",
      stateLabel: "மாநிலம்",
      statePlaceholder: "மாநிலத்தைத் தேர்ந்தெடுக்கவும்",
      pincodeLabel: "பின்கோடு",
      pincodePlaceholder: "6 இலக்க பின்கோடு",
      chipIndependent: "தனி",
      chipPgHostel: "பிஜி / விடுதி",
      chipCommercial: "வணிக",
      propertyNameLabel: "சொத்தின் பெயர்",
      propertyNamePlaceholder: "எ.கா. 3, ரோஸ்வுட் அபார்ட்மென்ட்ஸ்",
      typeLabel: "வகை",
      expectedRentLabel: "எதிர்பார்க்கும் வாடகை",
      expectedDepositLabel: "எதிர்பார்க்கும் டெபாசிட்",
      maintenanceLabel: "பராமரிப்பு / மாதம் (விருப்பத்திற்குரியது)",
      maintenancePlaceholder: "எ.கா. 1500",
      multiUnitChip: "இந்த சொத்தில் பல வீடுகள் உள்ளன",
      multiUnitHint: "வாடகைக்கு ஒன்றுக்கு மேற்பட்ட தனி வீடுகள்/அடுக்குமாடிகள் உள்ள வளாகம் அல்லது கட்டிடத்திற்கு — ஒவ்வொன்றிற்கும் தனி வாடகை, டெபாசிட், குடியிருப்பாளர் இருக்கும்.",
      commercialDetailsHeading: "வணிக விவரங்கள்",
      carpetAreaLabel: "கார்பெட் பரப்பளவு (சதுர அடி)",
      builtUpAreaLabel: "கட்டப்பட்ட பரப்பளவு (சதுர அடி)",
      lockInMonthsLabel: "லாக்-இன் காலம் (மாதங்கள்)",
      rentEscalationPercentLabel: "வாடகை உயர்வு (%)",
      rentEscalationYearsLabel: "எத்தனை ஆண்டுகளுக்கு ஒருமுறை உயர்வு",
      possessionStatusLabel: "உடைமை நிலை",
      possession_ready: "உடனே கிடைக்கும்",
      possession_under_fitout: "ஃபிட்-அவுட் நடைபெறுகிறது",
      washroomTypeLabel: "கழிவறை",
      washroom_attached: "இணைந்தது",
      washroom_shared: "பகிரப்பட்டது",
      commercialAmenitiesLabel: "வசதிகள்",
      powerBackupChip: "பவர் பேக்அப்",
      powerLoadDetailsLabel: "பவர் பேக்அப் விவரங்கள் (விருப்பத்தேர்வு)",
      powerLoadDetailsPlaceholder: "எ.கா. 3-பேஸ், 100% DG பேக்அப்",
      parkingSpotsLabel: "ஒதுக்கப்பட்ட பார்க்கிங் (கார்/பைக்)",
      gstChip: "வாடகையில் 18% GST பொருந்தும்",
      frontageChip: "முகப்பு / பெயர்ப்பலகை உரிமை",
      seatsCountLabel: "இருக்கைகள்",
      cabinsCountLabel: "கேபின்கள்",
      meetingRoomsCountLabel: "மீட்டிங் அறைகள்",
      housesHeading: "வீடுகள்",
      addHouseLink: "+ வீடு சேர் ›",
      houseNameLabel: "வீட்டின் பெயர்",
      hasExistingTenantChip: "இந்த சொத்தில் ஏற்கனவே ஒரு குடியிருப்பாளர் உள்ளார்",
      tenantNameLabel: "குடியிருப்பாளர் பெயர்",
      phoneLabel: "தொலைபேசி (பதிவு செய்திருந்தால், அவர்களின் ToLetBook கணக்குடன் பொருந்தும்)",
      phonePlaceholder: "10 இலக்க எண்",
      aadhaarLabel: "ஆதார் (விருப்பத்திற்குரியது)",
      dateOfOccupancyLabel: "குடியிருப்பு தொடங்கிய தேதி",
      dateHint: "அவர்கள் உண்மையில் குடிபெயர்ந்த தேதி — வாடகை மற்றும் டெபாசிட் மேலே கொடுக்கப்பட்ட எதிர்பார்ப்பு மதிப்புகளைப் பயன்படுத்தும்.",
      occupancyLabel: "வசிப்பு வகை",
      occBoys: "பாய்ஸ்",
      occGirls: "கேர்ல்ஸ்",
      occCoEd: "கோ-எட்",
      roomsHeading: "அறைகள்",
      addRoomLink: "+ அறையைச் சேர் ›",
      sharingTypeLabel: "பகிர்வு வகை",
      rentPerBedLabel: "வாடகை/படுக்கை",
      depositPerBedLabel: "டெபாசிட்/படுக்கை",
      bedsLabel: "படுக்கைகள்",
      mealPlanLabel: "உணவு வசதி",
      preferredTenantsLabel: "விருப்பமான குடியிருப்பாளர்கள்",
      meal_breakfast: "காலை உணவு",
      meal_lunch: "மதிய உணவு",
      meal_dinner: "இரவு உணவு",
      tenantType_student: "மாணவர்",
      tenantType_professional: "வேலை செய்பவர்",
      roomAc: "ஏசி",
      roomAttachedBathroom: "இணைந்த குளியலறை",
      addPropertyButton: "சொத்தைச் சேர்",
    },
    ownerLedgers: {
      justNow: "இப்போதுதான்",
      minsAgo: "{mins} நிமிடங்களுக்கு முன்",
      hoursAgo: "{hours} மணி நேரத்திற்கு முன்",
      daysAgo: "{days} நாட்களுக்கு முன்",
      emptyNoProperty: "நோட்புக்கைக் கண்காணிக்க முதலில் ஒரு சொத்தைச் சேர்க்கவும்.",
      heading: "நோட்புக்",
      emptyNoTenants: "இங்கே இன்னும் குடியிருப்பாளர்கள் இல்லை.",
      markedPaid: "செலுத்தியதாகக் குறிக்கப்பட்டது",
      reminderSent: "குடியிருப்பாளருக்கு நினைவூட்டல் அனுப்பப்பட்டது",
      emptyNoActiveTenants: "இங்கே இன்னும் செயலில் உள்ள குடியிருப்பாளர்கள் இல்லை.",
      otherCategory: "மற்றவை",
      pastTenantsHeading: "முந்தைய குடியிருப்பாளர்கள் · {count}",
      rentLedgerLabel: "வாடகை பதிவேடு · கடந்த 12 மாதங்கள்",
      paidFraction: "{paid}/{total} செலுத்தப்பட்டது",
      entriesLabel: "பதிவுகள்",
      rentPaid: "வாடகை செலுத்தப்பட்டது",
      rentDue: "வாடகை நிலுவை",
      markReceived: "பெறப்பட்டதாகக் குறி (பணமாக)",
      markingBusy: "குறிக்கிறது…",
      remindAgain: "🔔 மீண்டும் நினைவூட்டு",
      remind: "🔔 நினைவூட்டு",
      sendingBusy: "அனுப்புகிறது…",
      remindedAgo: "{time} நினைவூட்டப்பட்டது",
      closeButton: "‹ மூடு",
      refundLabel: "பணத்திரும்பப் பெறல்",
      refundedAmount: "{amount} திரும்பப் பெறப்பட்டது",
    },
    ownerReports: {
      emptyState: "இன்னும் பதிவேட்டில் செயல்பாடு இல்லை — வசூலைத் தொடங்க ஒரு குடியிருப்பாளரைச் சேர்க்கவும்.",
      heading: "அறிக்கைகள்",
      collected: "வசூலிக்கப்பட்டது",
      pending: "நிலுவை",
      paidOnRecord: "பதிவில் செலுத்தப்பட்டது",
      collectionsByMonth: "மாதவாரி வசூல்",
      monthTooltip: "{month}: {collected} வசூலிக்கப்பட்டது, {pending} நிலுவை",
      byProperty: "சொத்து வாரியாக",
      pendingAmount: "{amount} நிலுவை",
      exportCsvButton: "பதிவேட்டை CSV ஆக ஏற்றுமதி செய்",
      hint: "ஒவ்வொரு வரிசையும் ஒரு பதிவேட்டு பதிவிலிருந்து வருகிறது — இங்கே கூடுதலாக எண்கள் சேர்க்கப்படவில்லை.",
    },
    ownerRequests: {
      visitRequestsHeading: "வருகை கோரிக்கைகள்",
      newCount: "{count} புதியவை",
      emptyNoVisits: "இன்னும் வருகை கோரிக்கைகள் இல்லை.",
      maintenanceRequestsHeading: "பராமரிப்பு கோரிக்கைகள்",
      emptyNoRequests: "இன்னும் கோரிக்கைகள் எதுவும் இல்லை.",
      noDetailsProvided: "விவரங்கள் எதுவும் தரப்படவில்லை",
      startWork: "வேலையைத் தொடங்கு",
      markResolved: "தீர்க்கப்பட்டதாகக் குறி",
      requestUpdated: "கோரிக்கை புதுப்பிக்கப்பட்டது",
      visitConfirmed: "வருகை உறுதி செய்யப்பட்டது",
      visitDeclined: "வருகை நிராகரிக்கப்பட்டது",
      noPhoneOnFile: "தொலைபேசி எண் பதிவு செய்யப்படவில்லை",
      visitLine: "{property} · {date} அன்று {time} மணிக்கு",
      declineButton: "நிராகரி",
      confirmButton: "உறுதிப்படுத்து",
      statusOpen: "திறந்துள்ளது",
      statusInProgress: "செயலில் உள்ளது",
      statusResolved: "தீர்க்கப்பட்டது",
      visitStatusPending: "நிலுவையில்",
      visitStatusConfirmed: "உறுதி செய்யப்பட்டது",
      visitStatusDeclined: "நிராகரிக்கப்பட்டது",
      visitStatusCancelled: "ரத்து செய்யப்பட்டது",
      vacateRequestsHeading: "காலி செய்யும் அறிவிப்புகள்",
      emptyNoVacates: "இன்னும் காலி செய்யும் அறிவிப்புகள் இல்லை.",
      vacateLine: "{property} · {date} அன்று காலி செய்யப்படும்",
      noticeGivenLine: "{date} அன்று அறிவிப்பு கொடுக்கப்பட்டது",
      acknowledgeButton: "ஏற்றுக்கொள்",
      vacateAcknowledged: "காலி செய்யும் அறிவிப்பு ஏற்றுக்கொள்ளப்பட்டது",
      vacateStatusPending: "நிலுவையில்",
      vacateStatusAcknowledged: "ஏற்றுக்கொள்ளப்பட்டது",
      vacateStatusCancelled: "ரத்து செய்யப்பட்டது",
    },
    editPropertyForm: {
      errNameRequired: "சொத்திற்கு ஒரு பெயர் கொடுங்கள்",
      propertyUpdated: "சொத்து புதுப்பிக்கப்பட்டது",
      couldNotUpdate: "சொத்தைப் புதுப்பிக்க முடியவில்லை",
      localityLabel: "பகுதி",
      propertyNameLabel: "சொத்தின் பெயர்",
      typeLabel: "வகை",
      residentialGroup: "குடியிருப்பு",
      commercialGroup: "வணிக",
      expectedRentLabel: "எதிர்பார்க்கும் வாடகை",
      expectedDepositLabel: "எதிர்பார்க்கும் டெபாசிட்",
      maintenanceLabel: "பராமரிப்பு / மாதம் (விருப்பத்திற்குரியது)",
      occupancyLabel: "வசிப்பு வகை",
      occBoys: "பாய்ஸ்",
      occGirls: "கேர்ல்ஸ்",
      occCoEd: "கோ-எட்",
      facilitiesLabel: "வசதிகள்",
      facility_wifi: "வைஃபை",
      facility_meals: "உணவு",
      facility_laundry: "சலவை",
      facility_powerBackup: "பவர் பேக்அப்",
      facility_cctv: "சிசிடிவி",
      facility_parking: "பார்க்கிங்",
      facility_ac: "ஏசி",
      facility_tv: "டிவி",
      facility_washingMachine: "வாஷிங் மெஷின்",
      perHouseFurnishingHint: "பர்னிஷிங் ஒவ்வொரு வீட்டுக்கும் தனித்தனியாக அமைக்கப்படும் — கீழே ஒவ்வொரு வீட்டையும் திறந்து அமைக்கவும்.",
      furnishingStatusLabel: "பர்னிஷிங்",
      furnishing_unfurnished: "பர்னிஷ் செய்யப்படாதது",
      furnishing_semi_furnished: "செமி-பர்னிஷ்டு",
      furnishing_fully_furnished: "முழுவதும் பர்னிஷ் செய்யப்பட்டது",
      commercialFurnishing_unfurnished: "பேர் ஷெல்",
      commercialFurnishing_semi_furnished: "வார்ம் ஷெல்",
      commercialFurnishing_fully_furnished: "முழுவதும் பர்னிஷ் செய்யப்பட்டது",
      communityTypeLabel: "கம்யூனிட்டி வகை",
      community_gated: "கேட்டட் கம்யூனிட்டி",
      community_semi_gated: "செமி கேட்டட் கம்யூனிட்டி",
      community_villa: "வில்லா",
      community_apartment: "அபார்ட்மென்ட்",
      community_independent: "இண்டிபெண்டன்ட்",
      floorNumberLabel: "மாடி",
      floorNumberPlaceholder: "எ.கா. 3 அல்லது கிரவுண்ட்",
      floorChipLabel: "மாடி {floor}",
      furnishingItemsLabel: "பர்னிச்சர் & உபகரணங்கள்",
      furnishingItemsHint: "வாடகைதாரருக்கு ஒவ்வொன்றும் எத்தனை இருக்கும் என்பதை உள்ளிடவும்.",
      item_fan: "மின்விசிறிகள்",
      item_light: "லைட்கள்",
      item_ac: "ஏசி",
      item_fridge: "ஃபிரிட்ஜ்",
      item_washingMachine: "வாஷிங் மெஷின்",
      item_sofa: "சோஃபா",
      item_bed: "படுக்கை",
      item_heater: "வாட்டர் ஹீட்டர் (பாத்ரூம்)",
      item_chimney: "சிம்னி (கிச்சன்)",
      addFurnitureItemPlaceholder: "+ மரச்சாமான் / உபகரணம் சேர்க்க",
      customItemNamePlaceholder: "எ.கா. படிப்பு மேசை",
      addCustomItemBtn: "+ மற்றொரு பொருளைச் சேர்க்க",
    },
    endTenancyForm: {
      tenancyEnded: "வாடகைத் தொடர்பு முடிந்தது — திரும்பப் பெறும் தொகை விவரங்கள் குடியிருப்பாளருக்கு அனுப்பப்பட்டன",
      couldNotEnd: "வாடகைத் தொடர்பை முடிக்க முடியவில்லை",
      heading: "வாடகைத் தொடர்பை முடி — {name}",
      dateOfExitLabel: "வெளியேறும் தேதி",
      depositPaidLabel: "செலுத்தப்பட்ட டெபாசிட்",
      deductionsLabel: "கழிவுகள் (சேதங்கள், நிலுவைகள் போன்றவை)",
      deductionNoteLabel: "கழிவு குறிப்பு (விருப்பத்திற்குரியது)",
      deductionNotePlaceholder: "எ.கா. சுவர் வண்ணப்பூச்சு சரிசெய்தல்",
      refundableDepositLabel: "திரும்பப் பெறத்தக்க டெபாசிட்",
      confirmButton: "உறுதிசெய்து வாடகைத் தொடர்பை முடி",
      endingBusy: "முடிக்கிறது…",
    },
    propertyPhotos: {
      uploadFailed: "பதிவேற்றம் தோல்வியடைந்தது",
      photoRemoved: "படம் அகற்றப்பட்டது",
      couldNotRemove: "படத்தை அகற்ற முடியவில்லை",
      removePhotoAria: "படத்தை அகற்று",
    },
    photoStager: {
      photosLabel: "படங்கள் (விருப்பத்திற்குரியது)",
      couldNotRead: "அந்த படத்தைப் படிக்க முடியவில்லை",
      removePhotoAria: "படத்தை அகற்று",
    },
    photoPickerButtons: {
      addPhotoTitle: "படத்தைச் சேர்",
      takePhoto: "📷 புகைப்படம் எடு",
      chooseFromFiles: "🖼 கோப்புகளிலிருந்து தேர்வு செய்",
    },
    ownerPlans: {
      title: "திட்டங்கள்",
      currentPlanLabel: "தற்போதைய திட்டம்",
      trialBadge: "இலவச பிரீமியம் டிரையல்",
      trialEndsOn: "டிரையல் {date} அன்று முடிவடையும்",
      renewsOn: "{date} அன்று புதுப்பிக்கப்படும்",
      listingsUsed: "{cap} இல் {used} பட்டியல்கள் பயன்படுத்தப்பட்டன",
      billingMonthly: "மாதம்தோறும்",
      billingYearly: "ஆண்டுதோறும் · ~16% சேமிக்கவும்",
      basicName: "பேசிக்",
      proName: "புரோ",
      premiumName: "பிரீமியம்",
      mostPopular: "மிகவும் பிரபலமானது",
      powerOwners: "பவர் உரிமையாளர்கள்",
      basicSub: "இப்போதுதான் தொடங்கும் உரிமையாளர்களுக்கு",
      proSub: "சில சொத்துகள் உள்ள உரிமையாளர்களுக்கு",
      premiumSub: "பல சொத்துகளை நிர்வகிக்கும் உரிமையாளர்களுக்கு",
      featListing1: "1 செயலில் உள்ள பட்டியல்",
      featListing3: "3 வரை செயலில் உள்ள பட்டியல்கள்",
      featListingUnlimited: "வரம்பற்ற பட்டியல்கள்",
      featEnquiries: "குடியிருப்பாளர் விசாரணைகள் மற்றும் வருகை கோரிக்கைகள்",
      featLedger: "வாடகை வசூல் மற்றும் டிஜிட்டல் பதிவேடு",
      featAgreement: "சுய-சேவை வாடகை ஒப்பந்த ஜெனரேட்டர் — விரைவில் வருகிறது",
      featMarketplace: "TO-LET மார்க்கெட்பிளேஸில் தெரியும்",
      featEverythingBasic: "பேசிக்கில் உள்ள அனைத்தும்",
      featEverythingPro: "புரோவில் உள்ள அனைத்தும்",
      featMarketplaceSwitch: "TO-LET மார்க்கெட்பிளேஸ் சுவிட்ச் — எப்போது வேண்டுமானாலும் லைவ் செய்யுங்கள்",
      featFeatured: "தேடலில் சிறப்பு இடம்",
      featPriority: "ஒத்த-சொத்து பரிந்துரைகளில் முன்னுரிமை",
      featTopListing: "டாப் பட்டியல் — மார்க்கெட்பிளேஸ் தேடலின் மேலே பின் செய்யப்பட்டுள்ளது",
      featPriorityAgreement: "முன்னுரிமை வாடகை ஒப்பந்த சேவை (சட்ட ரீதியாக மதிப்பாய்வு செய்யப்பட்டது, விரைவான டர்ன்அரவுண்ட்) — விரைவில் வருகிறது",
      featPriorityVerification: "முன்னுரிமை அடையாள-சரிபார்ப்பு மதிப்பாய்வு",
      featPrioritySupport: "முன்னுரிமை ஆதரவு",
      currentPlanBtn: "தற்போதைய திட்டம்",
      switchToBtn: "{plan}க்கு மாறவும்",
      noChargeNote: "இன்னும் பணம் செலுத்த வேண்டியதில்லை — பில்லிங் அமைக்கப்படும் வரை திட்டங்களை இலவசமாக மாற்றலாம்.",
      switchedToast: "{plan} திட்டத்திற்கு மாற்றப்பட்டது",
      switchFailedToast: "திட்டத்தை மாற்ற முடியவில்லை",
      trialActiveNote: "நீங்கள் இலவச பிரீமியம் டிரையலில் உள்ளீர்கள் — அது முடியும் வரை ஒவ்வொரு பிரீமியம் அம்சமும் திறக்கப்பட்டுள்ளது.",
      cancelPlanBtn: "திட்டத்தை ரத்து செய்",
      securePaymentNote: "பணம் செலுத்துதல் Razorpay மூலம் பாதுகாப்பாக செயலாக்கப்படுகிறது.",
    },
  },
};
